import { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation, useRoute, useSearch } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Star, Clock, MapPin, Info, Plus, Minus, ShoppingCart, X, Users, Camera, Heart, Meh, ThumbsDown, Trophy, Bookmark, Truck, Package, CalendarClock, Edit2, Sparkles, PenLine, Loader2, AlertCircle, TrendingUp, ChevronDown, UtensilsCrossed, Phone, RefreshCw, Search } from 'lucide-react';
import { KudozIcon } from '@/components/KudozIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import ItemDetailModal, { ModifierGroup, MenuItem } from '@/components/ItemDetailModal';
import CreateReviewModal from '@/components/CreateReviewModal';
import OrderInitiationModal, { OrderConfigParams } from '@/components/OrderInitiationModal';
import { useOrderableMenu, OrderConfig, useOrderDelays } from '@/hooks/useOrderableMenu';
import { useBrowseMenu } from '@/hooks/useBrowseMenu';
import { OrderAvailabilityBanner } from '@/components/OrderAvailabilityBanner';
import { useOrderAvailability } from '@/hooks/useOrderAvailability';
import { useAsapQuoteRefresh } from '@/utils/asapQuote';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiRequest } from '@/lib/queryClient';
import { cn } from '@/lib/utils';
import KudozDisplay from '@/components/KudozDisplay';
import { KudozModule } from '@/components/KudozModule';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useRestaurantBookmark, useIsRestaurantBookmarked } from '@/hooks/useRestaurantBookmark';
import { useUserProfile } from '@/hooks/useUserProfile';
import { RestaurantHoursPopover } from '@/components/RestaurantHoursPopover';
import { PopularTimesChart } from '@/components/PopularTimesChart';
import { useOrderHistory, formatOrderDate, formatOrderTotal, type OrderHistoryItem } from '@/hooks/useOrderHistory';
import { ReorderValidationModal } from '@/components/ReorderValidationModal';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/AuthService';
import { proxyIfCdn } from '@/hooks/useAuthenticatedMedia';
import { Alert, AlertDescription } from '@/components/ui/alert';
import AuthPromptBanner from '@/components/AuthPromptBanner';
import { useSessionLocation } from '@/hooks/useSessionLocation';

type MenuDisplayMode = 'browse' | 'order';

type MenuItemType = MenuItem & {
  kudozPromotion?: {
    type: 'double' | 'bonus' | 'daily-special';
    amount?: number;
    label?: string;
  };
  priceDisplay?: string;
  hasPriceRange?: boolean;
  menuPeriods?: string[];
};

interface ScheduleOpening {
  open: number;
  close: number;
}

interface ScheduleDay {
  day: number;
  openings: ScheduleOpening[];
}

interface EatzySchedules {
  business?: ScheduleDay[];
  takeout?: ScheduleDay[];
  delivery?: ScheduleDay[];
  catering?: ScheduleDay[];
}

interface EatzyRatings {
  stars: number;
  reviews: number;
}

interface RestaurantData {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  latitude: string | null;
  longitude: string | null;
  cuisineType: string | null;
  priceRange: string | null;
  overallRating: string | null;
  totalReviews: number | null;
  phone: string | null;
  website: string | null;
  isActive: boolean | null;
  hours: unknown;
  imageUrl: string | null;
  orderingEnabled: boolean;
  eatzyClientId: string | null;
  formattedAddress?: string;
  minOrder?: number;
  deliveryFee?: number;
  deliveryAvailable?: boolean;
  pickupAvailable?: boolean;
  liveStatus?: {
    open: boolean;
    nextOpenAt?: number;
    nextCloseAt?: number;
    message?: string;
  };
  // New Eatzy fields
  description?: string;
  category?: string;
  logoImageUrl?: string;
  primaryImageUrl?: string;
  eatzyBannerUrl?: string;
  eatzySchedules?: EatzySchedules;
  eatzyTimezone?: string;
  eatzyRatings?: EatzyRatings;
  hoursJson?: unknown; // Restaurant operating hours in JSON format
  // Promo video for hero banner
  promoVideoUrl?: string;
  // Photo count for gallery
  photoCount?: number;
  // Featured/trending flags
  isFeatured?: boolean;
}

interface MenuCategory {
  name: string;
  description?: string | null;
  items: {
    id: string;
    name: string;
    description: string | null;
    price: string;
    imageUrl: string | null;
    isAvailable: boolean;
    allergens: string[] | null;
    modifiers?: {
      id: string;
      name: string;
      required: boolean;
      multiSelect: boolean;
      minSelect?: number;
      maxSelect?: number;
      options: {
        id: string;
        name: string;
        priceAdjustment: number;
        isDefault?: boolean;
        available: boolean;
      }[];
    }[];
  }[];
}

interface MenuData {
  restaurantId: string;
  restaurantName: string;
  totalItems: number;
  totalCategories: number;
  categories: MenuCategory[];
}

interface FriendPhoto {
  id: string;
  url: string;
  friendName: string;
  friendAvatar: string | null;
  rating: number;
  reviewText: string;
  visitDate: string;
}

interface CartItem {
  item: MenuItemType;
  quantity: number;
  selections: { [groupId: string]: string[] };
  totalPrice: number;
  specialInstructions?: string;
}

// Mock data for restaurants without Eatzy connection
// Mock friend photos removed - using real data from API

const mockMenuItems: MenuItemType[] = [
  {
    id: 'mock_1',
    name: 'House Special',
    description: 'Chef\'s signature dish with premium ingredients',
    price: 18.99,
    category: 'Entrees',
    popular: true,
    kudozPromotion: { type: 'double', label: '2x Kudoz' }
  },
  {
    id: 'mock_2',
    name: 'Classic Appetizer',
    description: 'Traditional starter with house-made sauce',
    price: 12.99,
    category: 'Appetizers',
    popular: true,
    modifiers: [
      {
        id: 'size',
        name: 'Size',
        required: true,
        maxSelect: 1,
        multiSelect: false,
        options: [
          { id: 'small', name: 'Small', price: 0 },
          { id: 'large', name: 'Large', price: 3 }
        ]
      }
    ]
  },
  {
    id: 'mock_3',
    name: 'Garden Fresh Salad',
    description: 'Mixed greens with seasonal vegetables and house dressing',
    price: 10.99,
    category: 'Salads',
    modifiers: [
      {
        id: 'dressing',
        name: 'Dressing',
        required: true,
        maxSelect: 1,
        multiSelect: false,
        options: [
          { id: 'ranch', name: 'Ranch', price: 0 },
          { id: 'italian', name: 'Italian', price: 0 },
          { id: 'balsamic', name: 'Balsamic Vinaigrette', price: 0.50 }
        ]
      }
    ]
  },
  {
    id: 'mock_4',
    name: 'Grilled Chicken',
    description: 'Herb-marinated chicken breast with seasonal sides',
    price: 16.99,
    category: 'Entrees'
  },
  {
    id: 'mock_5',
    name: 'Pasta Primavera',
    description: 'Fresh pasta with garden vegetables in light cream sauce',
    price: 14.99,
    category: 'Entrees'
  },
  {
    id: 'mock_6',
    name: 'Garlic Bread',
    description: 'Toasted bread with garlic butter and herbs',
    price: 5.99,
    category: 'Appetizers'
  },
  {
    id: 'mock_7',
    name: 'Chocolate Cake',
    description: 'Rich chocolate layer cake with ganache frosting',
    price: 7.99,
    category: 'Desserts',
    kudozPromotion: { type: 'bonus', amount: 25, label: '+25 Kudoz' }
  },
  {
    id: 'mock_8',
    name: 'Tiramisu',
    description: 'Classic Italian dessert with espresso and mascarpone',
    price: 6.99,
    category: 'Desserts'
  }
];

// Best designations are computed from restaurant data (not mock)
const getBestDesignations = (restaurant: RestaurantData | undefined): string[] => {
  const designations: string[] = [];
  const rating = restaurant?.overallRating ? parseFloat(restaurant.overallRating.toString()) : 0;
  if (rating >= 4.5) {
    designations.push('Top Rated');
  }
  if (restaurant?.orderingEnabled) {
    designations.push('Order Available');
  }
  return designations;
};

// Mock popular times data - to be replaced with real data
const mockPopularTimesEnabled = false; // Hidden - no real data source available for Popular Times

function formatUSPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function Restaurant() {
  const [currentLocation, navigate] = useLocation();
  const searchString = useSearch();
  const [, params] = useRoute('/restaurant/:id');
  const { selectedLocationName: sessionLocationName, customLatitude: sessionLat, customLongitude: sessionLng, setSessionLocation } = useSessionLocation();
  // Derive coordinates object once — passed as props to modals so they share this single source of truth
  const sessionCoords = typeof sessionLat === 'number' && typeof sessionLng === 'number'
    ? { lat: sessionLat, lng: sessionLng }
    : null;
  const restaurantId = params?.id;
  
  // Get user's addresses and phones for location display and pre-checkout validation
  const { primaryAddress, addresses, phones, refreshPhones, isLoading: isProfileLoading } = useUserProfile();
  
  // Fetch restaurant data from API
  const { data: restaurant, isLoading: restaurantLoading, error: restaurantError } = useQuery<RestaurantData>({
    queryKey: ['/api/restaurants', restaurantId],
    enabled: !!restaurantId,
  });

  // Fetch menu data from API
  const { data: menuData, isLoading: menuLoading, error: menuError } = useQuery<MenuData>({
    queryKey: ['/api/restaurants', restaurantId, 'menu'],
    enabled: !!restaurantId,
  });

  const { isAuthenticated, loginAsGuest } = useAuth();

  // Fetch user's reviews to find their rating for this restaurant (requires auth)
  const { data: userReviews } = useQuery<{ id: string; restaurantId: string; rating: number }[]>({
    queryKey: ['/api/reviews/me'],
    enabled: isAuthenticated,
  });
  
  // Find user's review for this specific restaurant
  const userReviewForRestaurant = userReviews?.find(review => review.restaurantId === restaurantId);
  const userRating = userReviewForRestaurant?.rating ?? null;
  
  // Fetch friends data for this restaurant (requires auth)
  const { data: friendsData } = useQuery<{
    friendsWhoVisited: { id: string; name: string; avatar: string | null }[];
    friendScore: number | null;
    friendPhotos: { id: string; url: string; friendName: string; friendAvatar: string | null; rating: number; text: string | null; createdAt: string | null }[];
  }>({
    queryKey: ['/api/restaurants', restaurantId, 'friends'],
    enabled: !!restaurantId && isAuthenticated,
  });
  
  // Fetch promotions data (does not require auth)
  const { data: promotionsData } = useQuery<{
    promotions: Array<{ id: string; name: string; awardValue: string; awardType: string }>;
    hasDoubleKudoz: boolean;
  }>({
    queryKey: ['/api/restaurants', restaurantId, 'promotions'],
    enabled: !!restaurantId,
  });
  
  // Fetch user stats for this restaurant (requires auth)
  const { data: userStatsData } = useQuery<{
    orderCount: number;
    totalSpent: number;
    lastOrderDate: string | null;
  }>({
    queryKey: ['/api/restaurants', restaurantId, 'user-stats'],
    enabled: !!restaurantId && isAuthenticated,
  });

  // Fetch personal + friend item reviews at this restaurant (auth-gated)
  const { data: myItemReviewsData } = useQuery<{
    personal: Array<{ itemName: string; rating: number; photoUrl?: string }>;
    friends: Array<{ itemName: string; rating: number; friendAvatars: string[]; count: number }>;
  }>({
    queryKey: ['/api/restaurants', restaurantId, 'my-item-reviews'],
    enabled: !!restaurantId && isAuthenticated,
    staleTime: 60000,
  });

  const personalItemMap = useMemo(() => {
    const map = new Map<string, { rating: number; photoUrl?: string }>();
    (myItemReviewsData?.personal ?? []).forEach(r => {
      map.set(r.itemName.toLowerCase(), { rating: r.rating, photoUrl: r.photoUrl });
    });
    return map;
  }, [myItemReviewsData]);

  const friendItemMap = useMemo(() => {
    const map = new Map<string, { rating: number; friendAvatars: string[]; count: number }>();
    (myItemReviewsData?.friends ?? []).forEach(r => {
      map.set(r.itemName.toLowerCase(), { rating: r.rating, friendAvatars: r.friendAvatars, count: r.count });
    });
    return map;
  }, [myItemReviewsData]);

  // Fetch similar restaurants by category
  const { data: similarRestaurants = [], isLoading: isSimilarLoading } = useQuery<{
    id: string;
    name: string;
    category: string | null;
    brandCategoryName: string | null;
    overallRating: string | null;
    logoImageUrl: string | null;
    primaryImageUrl: string | null;
    city: string;
    state: string;
  }[]>({
    queryKey: ['/api/restaurants', restaurantId, 'similar'],
    enabled: !!restaurantId,
  });

  const { data: orderHistoryData } = useOrderHistory(1, '', { enabled: isAuthenticated && !!restaurant?.eatzyClientId });

  const restaurantOrders = useMemo(() => {
    if (!orderHistoryData?.results || !restaurant?.eatzyClientId) return [];
    const completedStatuses = ['closed', 'delivered', 'completed'];
    return orderHistoryData.results
      .filter(order => 
        order.restaurant?.profile === restaurant.eatzyClientId &&
        completedStatuses.includes(order.status?.toLowerCase() || '')
      )
      .slice(0, 5);
  }, [orderHistoryData, restaurant?.eatzyClientId]);

  // Build social data from real API responses, with fallback to empty defaults
  const socialData = {
    friendsVisited: friendsData?.friendsWhoVisited ?? [],
    friendScore: friendsData?.friendScore ?? null,
    doubleKudoz: promotionsData?.hasDoubleKudoz ?? false,
    yourOrders: userStatsData?.orderCount ?? 0,
  };
  
  // Map API friend photos to FriendPhoto interface expected by the component
  const realFriendPhotos: FriendPhoto[] = (friendsData?.friendPhotos ?? []).map(photo => ({
    id: photo.id,
    url: photo.url,
    friendName: photo.friendName,
    friendAvatar: photo.friendAvatar,
    rating: photo.rating,
    reviewText: photo.text || '',
    visitDate: photo.createdAt ? new Date(photo.createdAt).toLocaleDateString() : '',
  }));

  const [cart, setCart] = useState<CartItem[]>([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [menuSearch, setMenuSearch] = useState('');
  const { toast } = useToast();
  const [selectedItem, setSelectedItem] = useState<MenuItemType | null>(null);
  const [editingCartIndex, setEditingCartIndex] = useState<number | null>(null);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [cartContextExpanded, setCartContextExpanded] = useState(false);
  const [selectedReview, setSelectedReview] = useState<FriendPhoto | null>(null);
  const [reorderModalData, setReorderModalData] = useState<{
    orderId: string;
    restaurantId: string;
    eatzyClientId: string;
    serviceType: 'takeout' | 'delivery';
    deliveryLat?: number;
    deliveryLng?: number;
    deliveryAddressId?: number;
  } | null>(null);
  
  // Restaurant bookmark functionality using API
  const { toggleBookmark, isToggling: isBookmarkToggling } = useRestaurantBookmark(restaurantId || '');
  const isBookmarked = useIsRestaurantBookmarked(restaurantId || '');
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isEditServiceModalOpen, setIsEditServiceModalOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isCartFooterExpanded, setIsCartFooterExpanded] = useState(false);
  
  // Order configuration state for Tier 2 restaurants
  const [orderConfig, setOrderConfig] = useState<OrderConfig | null>(null);
  
  // Menu display mode: 'browse' (no cart) or 'order' (with cart)
  // Tier 1 restaurants always stay in browse mode
  // Tier 2 restaurants start in browse mode, switch to order mode after context modal
  // Exception: when returning from checkout via "Modify Order" (?modifying=1), start in order mode
  const [menuDisplayMode, setMenuDisplayMode] = useState<MenuDisplayMode>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('modifying') === '1' ? 'order' : 'browse';
  });
  
  // State for showing order initiation modal (for scheduling when closed)
  const [showOrderInitiationModal, setShowOrderInitiationModal] = useState(false);
  
  // State for context change confirmation dialog
  const [pendingContextChange, setPendingContextChange] = useState<OrderConfigParams | null>(null);
  const [showContextChangeDialog, setShowContextChangeDialog] = useState(false);
  
  // Pre-checkout validation modal state
  const [showPreCheckoutModal, setShowPreCheckoutModal] = useState(false);
  const [preCheckoutNewPhone, setPreCheckoutNewPhone] = useState('');
  const [preCheckoutSubmittingPhone, setPreCheckoutSubmittingPhone] = useState(false);

  // Guest checkout sheet state (shown when unauthenticated user taps Checkout)
  const [guestCheckoutSheetOpen, setGuestCheckoutSheetOpen] = useState(false);
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestFields, setGuestFields] = useState({ firstName: '', lastName: '', phone: '', email: '' });
  const [guestErrors, setGuestErrors] = useState<Record<string, string>>({});
  const [guestLoading, setGuestLoading] = useState(false);
  const [guestServerError, setGuestServerError] = useState('');
  const [emailExistsWarning, setEmailExistsWarning] = useState(false);
  
  // Scroll-aware floating CTA: track when action buttons leave viewport
  const actionButtonsRef = useRef<HTMLDivElement>(null);
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  
  // Use Intersection Observer to detect when action buttons scroll out of view
  useEffect(() => {
    const actionButtonsEl = actionButtonsRef.current;
    if (!actionButtonsEl) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show floating CTA when action buttons are NOT visible
        setShowFloatingCTA(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-50px 0px 0px 0px' }
    );
    
    observer.observe(actionButtonsEl);
    return () => observer.disconnect();
  }, []);

  // Extract URL parameters for service selection
  // Use wouter's useSearch hook to get query params reliably
  // Include currentLocation as dependency to ensure recalculation on any URL change
  // Also include orderConfig to respect saved cart address when URL params aren't present
  const urlParams = useMemo(() => {
    // Fallback to window.location.search if searchString is empty (wouter quirk)
    const queryString = searchString || window.location.search;
    const params = new URLSearchParams(queryString);
    
    const addressIdParam = params.get('addressId');
    // Priority: URL param addressId → saved orderConfig.addressId → null
    const addressId = addressIdParam 
      ? parseInt(addressIdParam, 10) 
      : (orderConfig?.addressId ?? null);
    const locationParam = params.get('location');
    
    // Look up selected address by ID (from URL or saved orderConfig), or fall back to URL location param, or primary address
    const selectedAddress = addressId 
      ? addresses.find(a => a.id === addressId) 
      : null;
    
    // Priority: selected address by ID → URL location param → primary address → session location → default
    let locationDisplay: string;
    if (selectedAddress) {
      locationDisplay = `${selectedAddress.streetAddress}${selectedAddress.city ? `, ${selectedAddress.city}` : ''}`;
    } else if (locationParam) {
      // Use the location from URL params (set by the order modal)
      locationDisplay = locationParam;
    } else if (primaryAddress?.streetAddress) {
      locationDisplay = `${primaryAddress.streetAddress}${primaryAddress.city ? `, ${primaryAddress.city}` : ''}`;
    } else if (sessionLocationName) {
      locationDisplay = sessionLocationName;
    } else {
      locationDisplay = 'Select a location';
    }
    
    return {
      serviceType: (params.get('serviceType') as 'delivery' | 'takeout') || orderConfig?.serviceType || 'delivery',
      location: locationDisplay,
      timing: (params.get('timing') as 'asap' | 'scheduled') || orderConfig?.timing || 'asap',
      scheduledTime: params.get('scheduledTime') || (orderConfig?.scheduledStart ? new Date(orderConfig.scheduledStart * 1000).toISOString() : ''),
      radius: parseInt(params.get('radius') || '2', 10),
      addressId
    };
  }, [primaryAddress, addresses, searchString, currentLocation, orderConfig, sessionLocationName]);
  
  // Format scheduled time for display (converts ISO string to readable format)
  const formatScheduledTimeDisplay = (isoTime: string, timezone?: string): string => {
    if (!isoTime) return 'Scheduled';
    try {
      const date = new Date(isoTime);
      const tzOptions = timezone ? { timeZone: timezone } : {};
      
      const dateStr = date.toLocaleDateString('en-US', { ...tzOptions, year: 'numeric', month: '2-digit', day: '2-digit' });
      const now = new Date();
      const nowStr = now.toLocaleDateString('en-US', { ...tzOptions, year: 'numeric', month: '2-digit', day: '2-digit' });
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toLocaleDateString('en-US', { ...tzOptions, year: 'numeric', month: '2-digit', day: '2-digit' });
      
      const isToday = dateStr === nowStr;
      const isTomorrow = dateStr === tomorrowStr;
      
      const timeStr = date.toLocaleTimeString('en-US', {
        ...tzOptions,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
      
      if (isToday) {
        return `Today, ${timeStr}`;
      } else if (isTomorrow) {
        return `Tomorrow, ${timeStr}`;
      } else {
        const dayStr = date.toLocaleDateString('en-US', { ...tzOptions, weekday: 'short', month: 'short', day: 'numeric' });
        return `${dayStr}, ${timeStr}`;
      }
    } catch {
      return 'Scheduled';
    }
  };

  // Cart expiry time: 4 hours in milliseconds
  const CART_EXPIRY_MS = 4 * 60 * 60 * 1000;

  // Load cart from localStorage on mount - only if it belongs to this restaurant and is recent
  // URL params take priority - if present, skip cart restoration (URL params effect will handle order context)
  useEffect(() => {
    if (!restaurantId || !restaurant?.orderingEnabled) return;
    
    // Check if URL has fresh order context (from OrderInitiationModal)
    // URL params represent a fresh order session - start with empty cart unless saved cart
    // matches the same restaurant AND has compatible order context AND is recent
    const params = new URLSearchParams(window.location.search);
    const hasUrlOrderContext = params.has('serviceType') && params.has('location');
    if (hasUrlOrderContext) {
      console.log('[Cart] URL has order context (fresh from OrderInitiationModal)');
      const savedCartData = localStorage.getItem('eatzy-cart');
      
      if (savedCartData) {
        try {
          const parsed = JSON.parse(savedCartData);
          
          // Only restore cart if: same restaurant, recent, and has valid orderConfig
          // Also match against eatzyClientId for carts saved via the reorder flow
          const cartMatchesRestaurant = parsed.restaurantId === restaurantId ||
            (restaurant?.eatzyClientId && parsed.restaurantId === restaurant.eatzyClientId);
          if (cartMatchesRestaurant && 
              Array.isArray(parsed.items) && 
              parsed.items.length > 0 &&
              parsed.orderConfig && 
              parsed.timestamp) {
            
            const cartAge = Date.now() - parsed.timestamp;
            if (cartAge <= CART_EXPIRY_MS) {
              // Cart is valid and recent - load items (URL params effect will set orderConfig)
              console.log('[Cart] Restoring recent cart with', parsed.items.length, 'items (URL params will set order context)');
              setCart(parsed.items);
            } else {
              // Cart is stale - clear it
              console.log('[Cart] Saved cart is stale, starting fresh');
              localStorage.removeItem('eatzy-cart');
              setCart([]);
            }
          } else {
            // Cart doesn't belong to this restaurant or is invalid - clear it
            const cartBelongsHere = parsed.restaurantId === restaurantId ||
              (restaurant?.eatzyClientId && parsed.restaurantId === restaurant.eatzyClientId);
            if (!cartBelongsHere) {
              console.log('[Cart] Saved cart is for different restaurant, starting fresh');
            } else {
              console.log('[Cart] Saved cart is invalid (missing items/config), clearing');
              localStorage.removeItem('eatzy-cart');
            }
            setCart([]);
          }
        } catch (e) {
          console.log('[Cart] Parse error:', e);
          setCart([]);
        }
      } else {
        console.log('[Cart] No saved cart, starting fresh');
        setCart([]);
      }
      return;
    }
    
    const savedCartData = localStorage.getItem('eatzy-cart');
    console.log('[Cart] Loading from localStorage:', savedCartData);
    if (savedCartData) {
      try {
        const parsed = JSON.parse(savedCartData);
        console.log('[Cart] Parsed data:', parsed, 'Current restaurant:', restaurantId);
        
        // Check if cart belongs to this restaurant (match by local UUID or Eatzy client ID)
        const cartMatchesThisRestaurant = parsed.restaurantId === restaurantId ||
          (restaurant?.eatzyClientId && parsed.restaurantId === restaurant.eatzyClientId);
        if (!cartMatchesThisRestaurant) {
          console.log('[Cart] Different restaurant, showing empty cart');
          setCart([]);
          return;
        }
        
        // Check if cart is valid (has items)
        if (!Array.isArray(parsed.items) || parsed.items.length === 0) {
          console.log('[Cart] No items in saved cart');
          setCart([]);
          return;
        }
        
        // Check if cart is recent (within 4 hours)
        const cartTimestamp = parsed.timestamp || 0;
        const cartAge = Date.now() - cartTimestamp;
        if (cartAge > CART_EXPIRY_MS) {
          console.log('[Cart] Cart expired (age:', Math.round(cartAge / 1000 / 60), 'minutes), clearing');
          localStorage.removeItem('eatzy-cart');
          setCart([]);
          return;
        }
        
        // Check if we have valid orderConfig to restore
        if (!parsed.orderConfig || !parsed.orderConfig.serviceType) {
          console.log('[Cart] No valid order context saved with cart, clearing stale cart');
          localStorage.removeItem('eatzy-cart');
          setCart([]);
          return;
        }
        
        // Cart is valid, recent, and has order context - restore it
        console.log('[Cart] Restoring', parsed.items.length, 'items with order context');
        setCart(parsed.items);
        
        // Restore order config and switch to order mode
        setOrderConfig(parsed.orderConfig);
        setMenuDisplayMode('order');
        console.log('[Cart] Restored order mode with config:', parsed.orderConfig);
        
      } catch (e) {
        console.log('[Cart] Parse error:', e);
        setCart([]);
      }
    } else {
      console.log('[Cart] No saved cart found');
    }
  }, [restaurantId, restaurant?.orderingEnabled, restaurant?.eatzyClientId]);

  // Save cart to localStorage whenever it changes (with restaurantId, orderConfig, and timestamp)
  // Only save when cart has items and we have valid order context
  useEffect(() => {
    if (!restaurantId) return;
    
    console.log('[Cart] Cart state changed:', cart.length, 'items, orderConfig:', orderConfig);
    
    // Only update localStorage if we have items AND valid order context
    // This prevents saving carts without proper ordering context
    if (cart.length > 0 && orderConfig) {
      console.log('[Cart] Saving to localStorage with order context');
      localStorage.setItem('eatzy-cart', JSON.stringify({
        restaurantId,
        items: cart,
        orderConfig,
        timestamp: Date.now()
      }));
    } else if (cart.length === 0) {
      // Clear cart from localStorage when cart is emptied
      const savedCartData = localStorage.getItem('eatzy-cart');
      if (savedCartData) {
        try {
          const parsed = JSON.parse(savedCartData);
          if (parsed.restaurantId === restaurantId) {
            console.log('[Cart] Clearing empty cart from localStorage');
            localStorage.removeItem('eatzy-cart');
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    }
  }, [cart, restaurantId, orderConfig]);

  // Auto-configure order mode when URL params indicate order context from OrderInitiationModal
  // This handles navigation from review cards with pre-configured order settings
  useEffect(() => {
    if (!restaurant?.orderingEnabled) return;
    
    const params = new URLSearchParams(window.location.search);
    const hasOrderContext = params.has('serviceType') && params.has('location');
    
    // If URL has order context params, automatically set up order mode
    if (hasOrderContext && menuDisplayMode === 'browse') {
      const serviceType = params.get('serviceType') as 'delivery' | 'takeout';
      const timing = (params.get('timing') as 'asap' | 'scheduled') || 'asap';
      const scheduledTime = params.get('scheduledTime') || '';
      const addressIdParam = params.get('addressId');
      const addressId = addressIdParam ? parseInt(addressIdParam, 10) : undefined;
      
      // Look up address coordinates for map display on checkout
      const selectedAddr = addressId ? addresses.find(a => a.id === addressId) : null;
      
      console.log('[Restaurant] Auto-configuring order mode from URL params:', {
        serviceType, timing, scheduledTime, addressId, addressCoords: selectedAddr ? { lat: selectedAddr.latitude, lng: selectedAddr.longitude } : null
      });
      
      // Set up order config to trigger menu fetch
      // Include addressId and coordinates for delivery orders to ensure it propagates to checkout
      const config: OrderConfig = {
        serviceType,
        timing,
        addressId,
        // Store address coordinates for map display on checkout
        addressLatitude: selectedAddr?.latitude,
        addressLongitude: selectedAddr?.longitude,
      };
      
      // If scheduled, parse the scheduledTime into timestamp (in SECONDS to match OrderInitiationModal output)
      if (timing === 'scheduled' && scheduledTime) {
        const scheduledDate = new Date(scheduledTime);
        if (!isNaN(scheduledDate.getTime())) {
          // Store in seconds (not milliseconds) - useOrderAvailability expects seconds
          config.scheduledStart = Math.floor(scheduledDate.getTime() / 1000);
          // Set end time to 30 minutes after start (in seconds)
          config.scheduledEnd = Math.floor(scheduledDate.getTime() / 1000) + 30 * 60;
        }
      }
      
      setOrderConfig(config);
      
      // Switch to order mode
      setMenuDisplayMode('order');
    }
  }, [restaurant?.orderingEnabled, menuDisplayMode]);

  // Update orderConfig with address coordinates once addresses load (handles race condition)
  // This ensures the map on checkout page has coordinates even if addresses weren't loaded 
  // when the initial orderConfig was set
  useEffect(() => {
    if (!orderConfig || !orderConfig.addressId || !addresses.length) return;
    
    // Only update if we don't already have coordinates
    if (orderConfig.addressLatitude != null && orderConfig.addressLongitude != null) return;
    
    const selectedAddr = addresses.find(a => a.id === orderConfig.addressId);
    if (selectedAddr?.latitude != null && selectedAddr?.longitude != null) {
      console.log('[Restaurant] Updating orderConfig with address coordinates:', {
        addressId: orderConfig.addressId,
        lat: selectedAddr.latitude,
        lng: selectedAddr.longitude
      });
      setOrderConfig(prev => prev ? {
        ...prev,
        addressLatitude: selectedAddr.latitude,
        addressLongitude: selectedAddr.longitude,
      } : null);
    }
  }, [orderConfig?.addressId, orderConfig?.addressLatitude, orderConfig?.addressLongitude, addresses]);

  // Check if this restaurant has Eatzy connection (real menu data)
  const hasEatzyConnection = !!restaurant?.eatzyClientId;
  
  // Fetch availability-aware menu when ordering (only for Tier 2 with order config set)
  const { data: orderableMenuData, isLoading: orderableMenuLoading, isError: orderableMenuError } = useOrderableMenu(
    restaurant?.orderingEnabled ? restaurantId : undefined,
    orderConfig
  );
  
  // Fetch deduplicated browse menu for Tier 2 restaurants in browse mode
  const { data: browseMenuData, isLoading: browseMenuLoading, error: browseMenuError, isMenuEmpty } = useBrowseMenu(
    restaurant?.orderingEnabled ? restaurantId : undefined,
    menuDisplayMode === 'browse' && restaurant?.orderingEnabled
  );
  
  // Determine if we're in "ordering mode" (Tier 2 + order config set)
  const isOrderingMode = restaurant?.orderingEnabled && orderConfig !== null && menuDisplayMode === 'order';
  
  // Derive scheduledStart from orderConfig or URL params (convert ISO to seconds)
  const derivedScheduledStart = useMemo(() => {
    // Use orderConfig first if available
    if (orderConfig?.scheduledStart) {
      return orderConfig.scheduledStart;
    }
    // Fall back to URL scheduledTime (ISO string) - convert to seconds
    if (urlParams.scheduledTime) {
      const parsed = Date.parse(urlParams.scheduledTime);
      if (!isNaN(parsed)) {
        return Math.floor(parsed / 1000); // Convert ms to seconds
      }
    }
    return undefined;
  }, [orderConfig?.scheduledStart, urlParams.scheduledTime]);
  
  // Determine timing type for availability check
  const currentTiming = orderConfig?.timing || (urlParams.timing as 'asap' | 'scheduled') || 'asap';
  
  // For ASAP orders: menu fetch success is determined by whether orderableMenuData has items
  // This is the source of truth - if /menu/asap returns items, restaurant is available
  // Note: We only apply this check when orderConfig is set (ordering mode active)
  // When orderConfig is null, we're in browse mode or before order context is established
  const menuFetchSuccess = useMemo(() => {
    // Only apply ASAP availability check when in ordering mode
    if (currentTiming !== 'asap' || orderConfig === null) {
      return undefined; // Don't use menu check - scheduled orders or browse mode
    }
    
    // If fetch errored, restaurant is not available
    if (orderableMenuError) {
      return false;
    }
    
    // If still loading, let useOrderAvailability handle the loading state
    if (orderableMenuLoading) {
      return undefined; // Still loading, let hook decide
    }
    
    // Check if menu has items
    const hasItems = orderableMenuData?.categories && orderableMenuData.categories.length > 0;
    return hasItems ? true : false;
  }, [currentTiming, orderConfig, orderableMenuError, orderableMenuLoading, orderableMenuData]);
  
  // Use order availability hook for dynamic ASAP timing logic
  const orderAvailability = useOrderAvailability({
    restaurantId,
    liveStatus: restaurant?.liveStatus,
    serviceType: orderConfig?.serviceType || urlParams.serviceType || 'delivery',
    timing: currentTiming,
    scheduledStart: derivedScheduledStart,
    orderingEnabled: restaurant?.orderingEnabled,
    menuFetchSuccess,
    menuFetchLoading: currentTiming === 'asap' && orderConfig !== null ? orderableMenuLoading : undefined,
    timezone: restaurant?.eatzyTimezone,
  });
  
  // Derive closed state from availability hook - this is the source of truth for ordering
  const isRestaurantClosed = !orderAvailability.isAvailable;
  const nextOpenAt = orderAvailability.nextAvailableTime 
    ? Math.floor(orderAvailability.nextAvailableTime.getTime() / 1000)
    : restaurant?.liveStatus?.nextOpenAt;

  const isOrderUnavailable = !!(isOrderingMode && !orderAvailability.isAvailable && 
    (orderAvailability.bannerType === 'scheduled-unavailable' || 
    orderAvailability.bannerType === 'order-after-close' || 
    orderAvailability.bannerType === 'closed'));

  const isOrderTimeExpired = isOrderUnavailable && cart.length > 0;
  
  // Fetch delays for ASAP quote display
  const { data: delaysData } = useOrderDelays(restaurantId);
  
  // Memoize delays to prevent infinite loops in useAsapQuoteRefresh
  const menuDelays = useMemo(() => {
    if (!delaysData) return undefined;
    return {
      window: 900,
      delay: delaysData.takeoutSeconds,
      route: delaysData.deliverySeconds,
    };
  }, [delaysData?.takeoutSeconds, delaysData?.deliverySeconds]);
  
  // Get current service type for quote calculation
  const currentServiceType = orderConfig?.serviceType || urlParams.serviceType || 'delivery';
  
  // Auto-refreshing ASAP quote (updates every minute) - use restaurant timezone for display
  const asapQuote = useAsapQuoteRefresh(menuDelays, currentServiceType, restaurant?.eatzyTimezone);
  
  // Helper function to extract price from answer (handles both price and prices array)
  const getAnswerPrice = (answer: any, serviceType: 'delivery' | 'takeout'): number => {
    // First check prices array for service-specific pricing
    if (answer.prices && Array.isArray(answer.prices) && answer.prices.length > 0) {
      // Find price for current service type
      const servicePrice = answer.prices.find((p: any) => p.service === serviceType);
      if (servicePrice?.price !== undefined) {
        return servicePrice.price;
      }
      // Fallback to first price in array
      if (answer.prices[0]?.price !== undefined) {
        return answer.prices[0].price;
      }
    }
    // Use single price field
    if (answer.price !== undefined && answer.price !== null) {
      return answer.price;
    }
    return 0;
  };

  // Transform menu data from API to MenuItemType format, or use mock data as fallback
  const menuItems: MenuItemType[] = useMemo(() => {
    // For Tier 2 restaurants in browse mode, use deduplicated browse menu
    if (restaurant?.orderingEnabled && menuDisplayMode === 'browse' && browseMenuData?.categories) {
      return browseMenuData.categories.flatMap(category => 
        category.items.map(item => ({
          id: item.id,
          name: item.name,
          description: item.description || '',
          price: item.priceMin,
          priceDisplay: item.priceDisplay,
          hasPriceRange: item.priceMin !== item.priceMax,
          image: item.imageUrl || undefined,
          category: category.name,
          popular: false,
          menuPeriods: item.menuPeriods,
        }))
      );
    }
    
    // For Tier 2 restaurants in order mode, use orderableMenuData (has proper questions/answers with prices)
    if (isOrderingMode && orderableMenuData?.items && orderableMenuData.items.length > 0) {
      // Build category lookup from orderableMenuData
      const categoryMap = new Map<number, string>();
      orderableMenuData.categories?.forEach(cat => {
        categoryMap.set(cat.id, cat.name);
      });
      
      return orderableMenuData.items.map(item => {
        // Transform questions/answers to ModifierGroup format
        let modifiers: ModifierGroup[] | undefined;
        
        if (item.questions && item.questions.length > 0) {
          // Separate main questions from sub-questions; sort required first, then by order field
          const mainQuestions = item.questions
            .filter((q: any) => !q.sub)
            .sort((a: any, b: any) => {
              if (a.required !== b.required) return b.required ? 1 : -1;
              return (a.order ?? 0) - (b.order ?? 0);
            });
          const subQuestions = item.questions.filter((q: any) => q.sub);
          
          // Helper function to build nested modifiers for an answer
          const buildNestedModifiers = (answerId: string): ModifierGroup[] => {
            // Find sub-questions that are triggered by this answer
            // The linkage is encoded in the ID: sub-question ID starts with the parent answer ID followed by "_Q"
            // Example: Answer ID "I464753_Q462591_A464854" triggers sub-question "I464753_Q462591_A464854_Q489402"
            const nestedQuestions = subQuestions.filter((sq: any) => sq.id.startsWith(answerId + '_Q'));
            
            return nestedQuestions.map((sq: any) => {
              const nestedAnswers = (item.answers || []).filter((a: any) => a.parent === sq.id);
              const nestedDefaultMax = sq.format === 'checkbox' ? nestedAnswers.length : 1;
              const nestedApiMax = sq.max ?? nestedDefaultMax;
              const nestedApiMin = sq.min ?? (sq.required ? 1 : 0);
              const nestedMaxPerAnswer = sq.max_per_answer ?? 1;
              const nestedIsMultiSelect = sq.format === 'checkbox' || nestedApiMax > 1;
              
              return {
                id: sq.id,
                name: sq.name || sq.text,
                required: sq.required || false,
                multiSelect: nestedIsMultiSelect,
                minSelect: nestedApiMin,
                maxSelect: nestedApiMax,
                maxPerAnswer: nestedMaxPerAnswer,
                numFree: sq.num_free || undefined,
                options: nestedAnswers.map((a: any) => ({
                  id: a.id,
                  name: a.text,
                  price: getAnswerPrice(a, currentServiceType),
                  // Recursively build nested modifiers (for deeply nested questions)
                  nestedModifiers: buildNestedModifiers(a.id),
                })).map((opt: any) => ({
                  ...opt,
                  nestedModifiers: opt.nestedModifiers.length > 0 ? opt.nestedModifiers : undefined,
                })),
              };
            });
          };
          
          modifiers = mainQuestions.map((q: any) => {
            // Find answers that belong to this question
            const questionAnswers = (item.answers || []).filter((a: any) => a.parent === q.id);
            
            // Use min/max from API if available
            // For max: API value takes precedence, then max_per_answer, then format-based default
            // max_per_answer limits how many of a SINGLE option can be selected (e.g., 2x of same side)
            const defaultMax = q.format === 'checkbox' ? questionAnswers.length : 1;
            const apiMax = q.max ?? defaultMax;
            const apiMin = q.min ?? (q.required ? 1 : 0);
            const maxPerAnswer = q.max_per_answer ?? 1;
            
            // Multi-select if checkbox format OR max allows more than 1 selection
            const isMultiSelect = q.format === 'checkbox' || apiMax > 1;
            
            return {
              id: q.id,
              name: q.name || q.text,
              required: q.required || false,
              multiSelect: isMultiSelect,
              minSelect: apiMin,
              maxSelect: apiMax,
              maxPerAnswer: maxPerAnswer,
              numFree: q.num_free || undefined,
              options: questionAnswers.map((a: any) => {
                const nestedMods = buildNestedModifiers(a.id);
                return {
                  id: a.id,
                  name: a.text,
                  price: getAnswerPrice(a, currentServiceType),
                  nestedModifiers: nestedMods.length > 0 ? nestedMods : undefined,
                };
              }),
            };
          });
        }
        
        // Get price for current service type
        const itemPrice = item.prices?.find(p => p.service === currentServiceType)?.price 
          ?? item.prices?.[0]?.price 
          ?? 0;
        
        // Get category name
        const categoryName = categoryMap.get(item.category) || 'Menu';
        
        // Get image URL from media array if available
        const imageUrl = item.media?.[0]?.url || item.media?.[0]?.thumb || undefined;
        
        return {
          id: item.id,
          name: item.name,
          description: item.description || '',
          price: itemPrice,
          image: imageUrl,
          category: categoryName,
          popular: false,
          modifiers: modifiers && modifiers.length > 0 ? modifiers : undefined,
        };
      });
    }
    
    // If we have real menu data from API (generic menu endpoint), use it
    if (menuData?.categories && menuData.categories.length > 0) {
      return menuData.categories.flatMap(category => 
        category.items.map(item => {
          // Transform modifiers from API format to modal format
          // New format has { questions, answers } structure from Eatzy API
          let modifiers: ModifierGroup[] | undefined;
          const rawModifiers = item.modifiers as any;
          
          if (rawModifiers?.questions && Array.isArray(rawModifiers.questions)) {
            // New Eatzy API format: { questions: [...], answers: [...] }
            const answers = rawModifiers.answers || [];
            const sortedQuestions = [...rawModifiers.questions].sort((a: any, b: any) => {
              if (a.required !== b.required) return b.required ? 1 : -1;
              return (a.order ?? 0) - (b.order ?? 0);
            });
            modifiers = sortedQuestions.map((q: any) => {
              const questionAnswers = answers
                .filter((a: any) => a.parent === q.id)
                .sort((a: any, b: any) => (a.sort ?? 0) - (b.sort ?? 0));
              const isMultiSelect = q.format === 'checkbox' || (q.max && q.max > 1);
              
              return {
                id: q.id,
                name: q.name || q.text,
                required: q.required || false,
                multiSelect: isMultiSelect,
                minSelect: q.min || (q.required ? 1 : 0),
                maxSelect: q.max || (isMultiSelect ? questionAnswers.length : 1),
                numFree: q.num_free || undefined,
                options: questionAnswers.map((a: any) => ({
                  id: a.id,
                  name: a.text,
                  price: getAnswerPrice(a, currentServiceType),
                })),
              };
            });
          } else if (Array.isArray(rawModifiers)) {
            // Legacy array format (local database)
            modifiers = rawModifiers.map((group: any) => ({
              id: group.id,
              name: group.name,
              required: group.required,
              multiSelect: group.multiSelect,
              minSelect: group.minSelect,
              maxSelect: group.maxSelect,
              options: group.options?.map((option: any) => ({
                id: option.id,
                name: option.name,
                price: option.priceAdjustment || 0,
              })) || [],
            }));
          }

          return {
            id: item.id,
            name: item.name,
            description: item.description || '',
            price: parseFloat(item.price) || 0,
            image: item.imageUrl || undefined,
            category: category.name,
            popular: false,
            modifiers: modifiers && modifiers.length > 0 ? modifiers : undefined,
          };
        })
      );
    }
    
    // Return empty array if no real menu data available
    // User will see empty menu state rather than fake data
    return [];
  }, [menuData, browseMenuData, orderableMenuData, restaurant?.orderingEnabled, menuDisplayMode, isOrderingMode, currentServiceType]);
  
  // Use real friend photos from API only (no mock fallback)
  const friendPhotos: FriendPhoto[] = useMemo(() => {
    return realFriendPhotos;
  }, [realFriendPhotos]);

  // Build category descriptions lookup from menu data sources
  const categoryDescriptions = useMemo(() => {
    const descriptions = new Map<string, string>();
    
    // From browseMenuData (Tier 2 browse mode)
    if (browseMenuData?.categories) {
      browseMenuData.categories.forEach(cat => {
        if (cat.description) {
          descriptions.set(cat.name, cat.description);
        }
      });
    }
    
    // From orderableMenuData (Tier 2 order mode)
    if (orderableMenuData?.categories) {
      orderableMenuData.categories.forEach((cat: any) => {
        if (cat.description && !descriptions.has(cat.name)) {
          descriptions.set(cat.name, cat.description);
        }
      });
    }
    
    // From menuData (generic menu endpoint)
    if (menuData?.categories) {
      menuData.categories.forEach(cat => {
        if (cat.description && !descriptions.has(cat.name)) {
          descriptions.set(cat.name, cat.description);
        }
      });
    }
    
    return descriptions;
  }, [browseMenuData, orderableMenuData, menuData]);

  // Add "Friend Favorites" as the first tab - items recommended by friends
  const categories = ['Friend Favorites', 'All', ...Array.from(new Set(menuItems.map(item => item.category)))];
  
  // Filter menu based on active category and/or search query
  const filteredMenu = useMemo(() => {
    const q = menuSearch.trim().toLowerCase();

    // When searching, bypass category filter and search across all items
    if (q) {
      return menuItems.filter(item =>
        item.name.toLowerCase().includes(q) ||
        (item.description?.toLowerCase().includes(q) ?? false) ||
        (item.category?.toLowerCase().includes(q) ?? false)
      );
    }

    if (activeCategory === 'Friend Favorites') {
      // Show items with friend recommendations, or popular items if none
      const friendFavorites = menuItems.filter(item => 
        (item.friendRecommendations && item.friendRecommendations > 0) || item.popular
      );
      return friendFavorites.length > 0 ? friendFavorites : menuItems.slice(0, 5);
    }
    if (activeCategory === 'All') {
      return menuItems;
    }
    return menuItems.filter(item => item.category === activeCategory);
  }, [menuItems, activeCategory, menuSearch]);

  // Group menu items by category for "All" view (preserves category order)
  // Suppress grouping when search is active so results render as a flat list
  const groupedMenuItems = useMemo(() => {
    if (menuSearch.trim()) return null;
    if (activeCategory !== 'All') return null;
    
    // Get unique categories in order they appear
    const categoryOrder: string[] = [];
    const groups = new Map<string, MenuItemType[]>();
    
    menuItems.forEach(item => {
      if (!groups.has(item.category)) {
        categoryOrder.push(item.category);
        groups.set(item.category, []);
      }
      groups.get(item.category)!.push(item);
    });
    
    return categoryOrder.map(categoryName => ({
      name: categoryName,
      description: categoryDescriptions.get(categoryName),
      items: groups.get(categoryName) || []
    }));
  }, [menuItems, activeCategory, categoryDescriptions, menuSearch]);
  
  // Default tab: Menu (All) for Tier 2 ordering-enabled, Friend Favorites for Tier 1
  // Only run once when menu data becomes available
  const [hasSetInitialTab, setHasSetInitialTab] = useState(false);
  useEffect(() => {
    if (!hasSetInitialTab && menuItems.length > 0 && restaurant) {
      // Tier 2 restaurants prioritize ordering, so default to full menu
      // Tier 1 restaurants prioritize discovery, so default to Friend Favorites
      if (restaurant.orderingEnabled) {
        setActiveCategory('All');
      } else {
        setActiveCategory('Friend Favorites');
      }
      setHasSetInitialTab(true);
    }
  }, [menuItems.length, restaurant, hasSetInitialTab]);
  
  // Loading state
  const isLoading = restaurantLoading || menuLoading;
  const hasError = restaurantError || menuError;

  const normalizeSelections = (selections: { [groupId: string]: string[] }): { [groupId: string]: string[] } => {
    const normalized: { [groupId: string]: string[] } = {};
    Object.keys(selections).sort().forEach(key => {
      normalized[key] = [...selections[key]].sort();
    });
    return normalized;
  };

  // Calculate the minimum price from required modifiers for items with $0 base price
  const getMinimumRequiredModifierPrice = (item: MenuItemType): number => {
    if (!item.modifiers) return 0;
    
    let minTotal = 0;
    item.modifiers.forEach(group => {
      if (group.required && group.options.length > 0) {
        // Get sorted prices from lowest to highest
        const sortedPrices = group.options
          .map(opt => opt.price ?? 0)
          .sort((a, b) => a - b);
        
        // For multiSelect or groups requiring multiple selections, sum the N lowest prices
        const minSelectCount = group.minSelect ?? 1;
        const pricesToSum = sortedPrices.slice(0, Math.min(minSelectCount, sortedPrices.length));
        minTotal += pricesToSum.reduce((sum, price) => sum + price, 0);
      }
    });
    return minTotal;
  };

  // Get display price for menu items - shows "from $X.XX" for $0 items with required modifiers
  const getItemPriceDisplay = (item: MenuItemType): string => {
    // If the item already has a priceDisplay, use it
    if (item.priceDisplay) return item.priceDisplay;
    
    // If item has $0 price but has required modifiers, show "from $X.XX"
    if (item.price === 0 && item.modifiers?.some(g => g.required)) {
      const minModifierPrice = getMinimumRequiredModifierPrice(item);
      if (minModifierPrice > 0) {
        return `from $${minModifierPrice.toFixed(2)}`;
      }
    }
    
    return `$${item.price.toFixed(2)}`;
  };

  const calculateItemPrice = (
    item: MenuItemType,
    selections: { [groupId: string]: string[] }
  ): number => {
    let total = item.price;
    item.modifiers?.forEach(group => {
      const selectedOptions = selections[group.id] || [];
      const prices = selectedOptions
        .map(optionId => {
          const option = group.options.find(opt => opt.id === optionId);
          return option?.price || 0;
        })
        .filter(p => p > 0);

      if (group.numFree && group.numFree > 0 && prices.length > 0) {
        prices.sort((a, b) => a - b);
        const freeCount = Math.min(group.numFree, prices.length);
        for (let i = freeCount; i < prices.length; i++) {
          total += prices[i];
        }
      } else {
        prices.forEach(p => { total += p; });
      }
    });
    return total;
  };

  const addSimpleItemToCart = (item: MenuItemType) => {
    console.log('[Cart] addSimpleItemToCart called with item:', item.id, item.name);
    
    // Block cart additions when ordering is not available
    if (!orderAvailability.isAvailable) {
      console.log('[Cart] Blocked - ordering not available:', orderAvailability.bannerMessage);
      toast({
        description: orderAvailability.bannerMessage || 'Ordering is currently unavailable',
        variant: 'destructive',
      });
      // Open scheduling modal to let user pick a valid time
      setShowOrderInitiationModal(true);
      return;
    }
    
    const itemPrice = calculateItemPrice(item, {});
    const normalizedSelections = normalizeSelections({});
    const existingIndex = cart.findIndex(
      cartItem => 
        cartItem.item.id === item.id && 
        JSON.stringify(normalizeSelections(cartItem.selections)) === JSON.stringify(normalizedSelections)
    );

    console.log('[Cart] Existing index:', existingIndex, 'Current cart length:', cart.length);

    if (existingIndex >= 0) {
      setCart(prev => {
        const newCart = [...prev];
        newCart[existingIndex] = {
          ...newCart[existingIndex],
          quantity: newCart[existingIndex].quantity + 1
        };
        console.log('[Cart] Updated existing item, new quantity:', newCart[existingIndex].quantity);
        return newCart;
      });
    } else {
      setCart(prev => {
        const newCart = [...prev, {
          item,
          quantity: 1,
          selections: normalizedSelections,
          totalPrice: itemPrice
        }];
        console.log('[Cart] Added new item, new cart length:', newCart.length);
        return newCart;
      });
    }
  };

  const handleItemClick = (item: MenuItemType) => {
    // If ordering is not available and we're in browse mode, trigger scheduling flow
    if (!orderAvailability.isAvailable && menuDisplayMode === 'browse' && restaurant?.orderingEnabled) {
      setShowOrderInitiationModal(true);
      return;
    }
    
    // Always open item detail modal for all items
    // This allows users to see full item info, photos/videos, friend recommendations, and add special instructions
    setSelectedItem(item);
  };
  
  // Handler for "Start Your Order" button in browse mode
  const handleStartOrder = () => {
    setShowOrderInitiationModal(true);
  };
  
  // Handler for scheduling order from closed restaurant banner
  const handleScheduleOrder = () => {
    setShowOrderInitiationModal(true);
  };

  // Guest checkout helpers
  const handleGuestFieldChange = (field: keyof typeof guestFields) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'phone' ? formatUSPhone(e.target.value) : e.target.value;
    setGuestFields(prev => ({ ...prev, [field]: value }));
    setGuestErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'email') setEmailExistsWarning(false);
  };

  const handleGuestEmailBlur = async () => {
    const email = guestFields.email.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    try {
      const res = await fetch(`/api/auth/guest/check-email?email=${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        setEmailExistsWarning(data.exists && !data.isGuest);
      }
    } catch {}
  };

  const validateGuestFields = () => {
    const errs: Record<string, string> = {};
    if (!guestFields.firstName.trim()) errs.firstName = 'Required';
    if (!guestFields.lastName.trim()) errs.lastName = 'Required';
    if (!guestFields.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestFields.email))
      errs.email = 'Valid email required';
    const phoneDigits = guestFields.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) errs.phone = 'Valid 10-digit US phone required';
    setGuestErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleGuestCheckoutSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateGuestFields()) return;
    setGuestServerError('');
    setGuestLoading(true);
    const result = await loginAsGuest({
      firstName: guestFields.firstName.trim(),
      lastName: guestFields.lastName.trim(),
      email: guestFields.email.trim(),
      phone: guestFields.phone.replace(/\D/g, ''),
    });
    setGuestLoading(false);
    if (result.success) {
      setGuestCheckoutSheetOpen(false);
      // Guest account now exists — re-run the same checkout validation before navigating.
      // For guest users, phone comes from the form (not the profile addresses query which
      // may not have loaded yet), so we skip the phone check and proceed to checkout.
      // Address was already collected as part of the order config flow.
      const serviceType = urlParams.serviceType || orderConfig?.serviceType || 'delivery';

      // For delivery orders with a session location but no saved address, save the location as an address
      // on the new guest account. Use orderConfig coords (set by the modal) as the authoritative source.
      const guestAddrLat = orderConfig?.addressLatitude ?? sessionCoords?.lat;
      const guestAddrLng = orderConfig?.addressLongitude ?? sessionCoords?.lng;
      const guestAddrName = urlParams.location || sessionLocationName;
      if (
        serviceType === 'delivery' &&
        !orderConfig?.addressId &&
        guestAddrName &&
        typeof guestAddrLat === 'number' &&
        typeof guestAddrLng === 'number'
      ) {
        try {
          // Use authService.eatzyApiCall so the request goes through proxyWithUserToken,
          // which picks up the Eatzy native token (x-user-eatzy-token) and adds
          // X-Services-Secret. Using apiRequest here would only forward the Kudoz JWT
          // which may be rejected by the Eatzy API for address creation.
          const addrRes = await authService.eatzyApiCall('/account/address/new', {
            method: 'PUT',
            body: JSON.stringify({
              data: {
                address: guestAddrName,
                city: '',
                state: '',
                zip: '',
                primary: true,
                latitude: guestAddrLat,
                longitude: guestAddrLng,
                entered_address: guestAddrName,
                addition: { label: 'Delivery address' },
              },
            }),
          });
          if (addrRes.ok) {
            const addrData = await addrRes.json();
            // Raw Eatzy API returns { success: true, address: { address_id: ..., ... } }
            const newAddressId = addrData.address?.address_id ?? addrData.address_id ?? addrData.address?.id;
            if (newAddressId) {
              setOrderConfig(prev => prev
                ? { ...prev, addressId: newAddressId }
                : { serviceType: 'delivery', timing: 'asap', addressId: newAddressId }
              );
              // Synchronously write the addressId to localStorage before navigating.
              // setOrderConfig only updates React state; the useEffect that persists to
              // localStorage runs asynchronously and may not fire before Checkout reads it.
              try {
                const cartRaw = localStorage.getItem('eatzy-cart');
                const cartData = cartRaw ? JSON.parse(cartRaw) : {};
                if (!cartData.orderConfig) cartData.orderConfig = {};
                cartData.orderConfig.addressId = newAddressId;
                localStorage.setItem('eatzy-cart', JSON.stringify(cartData));
              } catch (storageErr) {
                console.debug('[GuestCheckout] localStorage write failed:', storageErr);
              }
            }
          } else {
            console.error('[GuestCheckout] Address save returned error status:', addrRes.status);
          }
        } catch (err) {
          console.error('[GuestCheckout] Address save failed:', err);
          // Non-fatal — proceed to checkout; Checkout page will prompt for address
        }
      }

      const hasAddress = serviceType === 'delivery'
        ? !!(urlParams.addressId || orderConfig?.addressId || primaryAddress || (typeof guestAddrLat === 'number' && typeof guestAddrLng === 'number'))
        : true;
      if (!hasAddress) {
        toast({
          title: 'Delivery address required',
          description: 'Please add a delivery address before checking out.',
          variant: 'destructive',
        });
        return;
      }
      navigate('/checkout');
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } else if (result.errorCode === 'EMAIL_EXISTS' || result.error?.toLowerCase().includes('email')) {
      setGuestServerError('An account with this email already exists. Please sign in instead.');
    } else {
      setGuestServerError(result.error || 'Something went wrong. Please try again.');
    }
  };

  const handleAddToCartFromModal = (
    item: MenuItemType,
    quantity: number,
    selections: { [groupId: string]: string[] },
    specialInstructions?: string
  ) => {
    // Block cart additions when ordering is not available
    if (!orderAvailability.isAvailable) {
      console.log('[Cart] Blocked from modal - ordering not available:', orderAvailability.bannerMessage);
      toast({
        description: orderAvailability.bannerMessage || 'Ordering is currently unavailable',
        variant: 'destructive',
      });
      setSelectedItem(null);
      setShowOrderInitiationModal(true);
      return;
    }
    
    const normalizedSelections = normalizeSelections(selections);
    const unitPrice = calculateItemPrice(item, normalizedSelections);

    // Edit mode: replace the cart item at the editing index in-place
    if (editingCartIndex !== null) {
      setCart(prev => {
        const newCart = [...prev];
        newCart[editingCartIndex] = {
          item,
          quantity,
          selections: normalizedSelections,
          totalPrice: unitPrice,
          specialInstructions
        };
        return newCart;
      });
      setEditingCartIndex(null);
      return;
    }
    
    // When matching existing cart items, also consider special instructions
    const existingIndex = cart.findIndex(
      cartItem => 
        cartItem.item.id === item.id && 
        JSON.stringify(normalizeSelections(cartItem.selections)) === JSON.stringify(normalizedSelections) &&
        (cartItem.specialInstructions || '') === (specialInstructions || '')
    );

    if (existingIndex >= 0) {
      setCart(prev => {
        const newCart = [...prev];
        newCart[existingIndex] = {
          ...newCart[existingIndex],
          quantity: newCart[existingIndex].quantity + quantity
        };
        return newCart;
      });
    } else {
      setCart(prev => [...prev, {
        item,
        quantity,
        selections: normalizedSelections,
        totalPrice: unitPrice,
        specialInstructions
      }]);
    }
  };

  const removeFromCart = (index: number) => {
    setCart(prev => {
      const newCart = [...prev];
      if (newCart[index].quantity > 1) {
        newCart[index] = {
          ...newCart[index],
          quantity: newCart[index].quantity - 1
        };
      } else {
        newCart.splice(index, 1);
      }
      return newCart;
    });
  };

  const addOneToCart = (index: number) => {
    setCart(prev => {
      const newCart = [...prev];
      newCart[index] = {
        ...newCart[index],
        quantity: newCart[index].quantity + 1
      };
      return newCart;
    });
  };

  const getItemCartQuantity = (itemId: string): number => {
    return cart
      .filter(cartItem => cartItem.item.id === itemId)
      .reduce((sum, cartItem) => sum + cartItem.quantity, 0);
  };

  // Helper to normalize scheduled time to milliseconds timestamp for comparison
  const normalizeScheduledTime = (time: number | string | undefined): number | undefined => {
    if (time === undefined) return undefined;
    if (typeof time === 'number') {
      // If it looks like seconds (smaller than year 2001 in ms), convert to ms
      return time < 1000000000000 ? time * 1000 : time;
    }
    if (typeof time === 'string') {
      const parsed = Date.parse(time);
      return isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  };

  // Helper to detect if order context has materially changed
  const hasContextChanged = (params: OrderConfigParams): boolean => {
    // Use orderConfig as primary source, fall back to URL params
    const currentServiceType = orderConfig?.serviceType || urlParams.serviceType;
    const currentTiming = orderConfig?.timing || urlParams.timing;
    
    // Check service type change
    if (currentServiceType && currentServiceType !== params.serviceType) {
      console.log('[Context] Service type changed:', currentServiceType, '->', params.serviceType);
      return true;
    }
    
    // Check timing change (asap vs scheduled)
    if (currentTiming && currentTiming !== params.timing) {
      console.log('[Context] Timing changed:', currentTiming, '->', params.timing);
      return true;
    }
    
    // Check scheduled time change (for scheduled orders)
    if (params.timing === 'scheduled') {
      // Normalize current scheduled time from either orderConfig or URL params
      const currentScheduledMs = normalizeScheduledTime(orderConfig?.scheduledStart) || 
                                  normalizeScheduledTime(urlParams.scheduledTime);
      // Normalize new scheduled time from params (prefer scheduledStart, then scheduledTime)
      const newScheduledMs = normalizeScheduledTime(params.scheduledStart) ||
                             normalizeScheduledTime(params.scheduledTime);
      
      // Compare normalized timestamps (allow 1 minute tolerance for rounding)
      if (currentScheduledMs && newScheduledMs) {
        const diff = Math.abs(currentScheduledMs - newScheduledMs);
        if (diff > 60000) { // More than 1 minute difference
          console.log('[Context] Scheduled time changed:', new Date(currentScheduledMs), '->', new Date(newScheduledMs));
          return true;
        }
      }
    }
    
    return false;
  };
  
  // Apply the context change (called directly or after confirmation)
  const applyContextChange = (params: OrderConfigParams, clearCart: boolean = false) => {
    // Clear cart if requested
    if (clearCart) {
      setCart([]);
      console.log('[Cart] Cleared due to context change');
    }
    
    // For Tier 2 restaurants, store the order configuration and switch to order mode
    if (restaurant?.orderingEnabled) {
      setOrderConfig({
        serviceType: params.serviceType,
        timing: params.timing,
        scheduledStart: params.scheduledStart,
        scheduledEnd: params.scheduledEnd,
        addressId: params.addressId,
        // Store address coordinates for map display on checkout
        addressLatitude: params.coordinates?.lat,
        addressLongitude: params.coordinates?.lng,
      });
      // Switch to order mode after context is set
      setMenuDisplayMode('order');
    }
    
    // Update URL with new parameters
    const searchParams = new URLSearchParams();
    searchParams.set('serviceType', params.serviceType);
    searchParams.set('location', params.location);
    searchParams.set('timing', params.timing);
    if (params.scheduledTime) {
      searchParams.set('scheduledTime', params.scheduledTime);
    }
    if (params.radius) {
      searchParams.set('radius', params.radius.toString());
    }
    // Include address ID for delivery orders
    if (params.addressId) {
      searchParams.set('addressId', params.addressId.toString());
    }
    
    // Navigate to same page with updated params
    navigate(`/restaurant/${restaurantId}?${searchParams.toString()}`);
    setIsEditServiceModalOpen(false);
    setShowOrderInitiationModal(false);
  };

  const handleServiceSettingsUpdate = (params: OrderConfigParams) => {
    // Validate: reject ASAP timing when restaurant is closed and ordering not available
    if (!orderAvailability.isAvailable && params.timing === 'asap') {
      toast({
        title: "Ordering not available",
        description: orderAvailability.bannerMessage || "Please select a scheduled time for your order.",
        variant: "destructive",
      });
      return;
    }
    
    // If cart has items and context has changed, show confirmation dialog
    if (cart.length > 0 && hasContextChanged(params)) {
      setPendingContextChange(params);
      setShowContextChangeDialog(true);
      return;
    }
    
    // No cart or no change - apply directly
    applyContextChange(params, false);
  };
  
  // Handle confirmation of context change (clears cart)
  const handleConfirmContextChange = () => {
    if (pendingContextChange) {
      applyContextChange(pendingContextChange, true);
      setPendingContextChange(null);
      setShowContextChangeDialog(false);
    }
  };
  
  // Handle cancellation of context change
  const handleCancelContextChange = () => {
    setPendingContextChange(null);
    setShowContextChangeDialog(false);
  };

  const cartItemCount = cart.reduce((sum, cartItem) => sum + cartItem.quantity, 0);
  const cartTotal = cart.reduce((sum, cartItem) => sum + (cartItem.totalPrice * cartItem.quantity), 0);
  // Amount still needed to meet the restaurant's minimum order requirement (0 when already met or no minimum)
  const belowMinOrder = restaurant?.minOrder && restaurant.minOrder > 0 && cartTotal > 0
    ? Math.max(0, restaurant.minOrder - cartTotal)
    : 0;

  // Derived restaurant display values with fallbacks
  // Per MENU_ORDERING_STATES.md: Use orderAvailability.isAvailable (from menu fetch) as source of truth
  // NOT liveStatus.open which is informational only
  const deliveryTime = useMemo(() => {
    // If restaurant is available for ordering (menu fetch succeeded), show ASAP quote
    if (!isRestaurantClosed) {
      if (asapQuote) {
        return asapQuote.formattedRange;
      }
      return '25-35 min'; // Fallback while loading
    }
    // Restaurant is closed for ordering
    if (urlParams.timing === 'scheduled' && urlParams.scheduledTime) {
      return 'Scheduled';
    }
    return 'Closed';
  }, [isRestaurantClosed, asapQuote, urlParams.timing, urlParams.scheduledTime]);
  const deliveryFee = restaurant?.deliveryFee || 0;
  
  // Build full address including street when available
  // Prefer formattedAddress when street address parts are missing
  const hasStreetParts = restaurant?.address || (restaurant?.city && restaurant?.state);
  const formattedAddress = hasStreetParts 
    ? [restaurant?.address, restaurant?.city, restaurant?.state, restaurant?.zipCode].filter(Boolean).join(', ')
    : (restaurant?.formattedAddress || 'Address not available');
  
  const cuisineTypes = restaurant?.cuisineType ? restaurant.cuisineType.split(',').map(c => c.trim()) : [];
  
  // Use app's own ratings (overallRating and totalReviews), not Eatzy ratings
  const rating = restaurant?.overallRating ? parseFloat(restaurant.overallRating) : null;
  const reviewCount = restaurant?.totalReviews ?? 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-background items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="mt-4 text-muted-foreground">Loading restaurant...</p>
      </div>
    );
  }

  // Error state
  if (hasError || !restaurant) {
    return (
      <div className="flex flex-col h-full bg-background items-center justify-center p-4">
        <AlertCircle className="w-12 h-12 text-destructive mb-4" />
        <h2 className="text-xl font-semibold mb-2">Restaurant not found</h2>
        <p className="text-muted-foreground text-center mb-4">
          We couldn't load this restaurant. It may have been removed or is temporarily unavailable.
        </p>
        <Button onClick={() => navigate('/')} data-testid="button-go-home">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>
      </div>
    );
  }

  // Determine if restaurant is trending (15+ reviews in past 7 days)
  // Uses real recentReviewCount from API, falls back to isFeatured flag
  const recentReviewCount = (restaurant as any).recentReviewCount ?? 0;
  const isTrending = recentReviewCount >= 15 || restaurant.isFeatured;
  
  // Photo count from real API data (reviews with photos)
  const photoCount = (restaurant as any).photoCount ?? 0;

  return (
    <div className="h-full bg-background lg:grid lg:grid-cols-[1fr_380px]">
      {/* Left: Main scrollable content */}
      <div className="flex flex-col min-w-0 overflow-y-auto h-full">
      {/* Header with Video/Banner */}
      <div className="relative">
        {/* Video or Image Hero */}
        {restaurant.promoVideoUrl ? (
          <video
            src={restaurant.promoVideoUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="w-full h-56 object-cover"
            poster={proxyIfCdn(restaurant.eatzyBannerUrl || restaurant.primaryImageUrl) || restaurant.imageUrl || undefined}
          />
        ) : (
          <img
            src={proxyIfCdn(restaurant.eatzyBannerUrl || restaurant.primaryImageUrl || restaurant.logoImageUrl) || restaurant.imageUrl || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800'}
            alt={restaurant.name}
            className="w-full h-56 object-cover"
          />
        )}
        
        {/* Bottom overlays: Trending badge and Photo count */}
        <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
          {/* Trending badge */}
          {isTrending && (
            <Badge className="bg-orange-500/90 text-white border-0 backdrop-blur-sm gap-1">
              <TrendingUp className="w-3 h-3" />
              Trending
            </Badge>
          )}
          {!isTrending && <div />}
          
          {/* Photo count */}
          {photoCount > 0 && (
            <Badge className="bg-black/60 text-white border-0 backdrop-blur-sm gap-1">
              <Camera className="w-3 h-3" />
              {photoCount} photos
            </Badge>
          )}
        </div>
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <Button
            variant="default"
            size="icon"
            onClick={() => {
              const beforeUrl = window.location.href;
              window.history.back();
              setTimeout(() => {
                if (window.location.href === beforeUrl) {
                  navigate('/');
                }
              }, 150);
            }}
            className="bg-background/90 text-foreground backdrop-blur-sm shadow-md rounded-full"
            data-testid="button-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Button
            variant="default"
            size="icon"
            disabled={isBookmarkToggling}
            onClick={() => {
              const willBeSaved = !isBookmarked;
              toggleBookmark();
              
              const hasEarnedFirstSaveBonus = localStorage.getItem('hasEarnedFirstSaveBonus');
              
              if (willBeSaved) {
                if (!hasEarnedFirstSaveBonus) {
                  localStorage.setItem('hasEarnedFirstSaveBonus', 'true');
                  toast({
                    description: (
                      <div className="flex items-center gap-2">
                        <Bookmark className="w-4 h-4 fill-current text-kudoz" />
                        <div>
                          <div className="font-medium">Saved to your list!</div>
                          <div className="text-sm text-muted-foreground">+10 Kudoz bonus for your first save</div>
                        </div>
                      </div>
                    )
                  });
                } else {
                  toast({
                    description: (
                      <div className="flex items-center gap-2">
                        <Bookmark className="w-4 h-4 fill-current text-kudoz" />
                        <span>Saved {restaurant.name}</span>
                      </div>
                    )
                  });
                }
              } else {
                toast({
                  description: `Removed ${restaurant.name} from saved`
                });
              }
            }}
            className={cn(
              "bg-background/90 text-foreground backdrop-blur-sm shadow-md rounded-full",
              isBookmarked && "text-kudoz"
            )}
            data-testid="button-bookmark"
          >
            <Bookmark className={cn("w-4 h-4 transition-transform", isBookmarked && "fill-current scale-110")} />
          </Button>
        </div>
      </div>

      <AuthPromptBanner />

      {/* Restaurant Info */}
      <div className="px-4 py-4 border-b space-y-3">
        {/* Row 1: Logo + Name + Rating + Price */}
        <div className="flex gap-3 items-start">
          {/* Restaurant Logo */}
          {restaurant.logoImageUrl && (
            <Avatar className="w-14 h-14 border-2 border-background shadow-md flex-shrink-0">
              <AvatarImage src={proxyIfCdn(restaurant.logoImageUrl)} alt={`${restaurant.name} logo`} />
              <AvatarFallback className="text-lg font-bold bg-primary/10 text-primary">
                {restaurant.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
          )}
          
          <div className="flex-1 min-w-0">
            {/* Name + Rating + Price on same conceptual row */}
            <div className="flex items-start justify-between gap-2">
              <h1 className="text-2xl font-bold leading-tight">{restaurant.name}</h1>
              <div className="flex items-center gap-2 flex-shrink-0">
                {rating !== null && (
                  <div className="flex items-center gap-1" data-testid="average-rating">
                    <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                    <span className="font-semibold">{rating.toFixed(1)}</span>
                    <span className="text-xs text-muted-foreground">({reviewCount})</span>
                  </div>
                )}
                {restaurant.priceRange && (
                  <span className="text-sm text-muted-foreground">{restaurant.priceRange}</span>
                )}
              </div>
            </div>
            
            {/* Row 2: Category + Best Designations */}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-sm text-muted-foreground">
                {restaurant.category || (cuisineTypes.length > 0 ? cuisineTypes.join(' • ') : 'Restaurant')}
              </span>
              {getBestDesignations(restaurant).map((designation) => (
                <Badge key={designation} className="text-xs px-1.5 py-0 bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30">
                  <Trophy className="w-3 h-3 mr-0.5" />
                  {designation}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Row 3: Address with distance */}
        <div className="text-sm text-muted-foreground flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{formattedAddress}</span>
          <span className="text-muted-foreground">·</span>
          <span className="font-medium text-foreground">1.2 mi</span>
        </div>

        {/* Row 4: Hours with Popover */}
        <RestaurantHoursPopover 
          liveStatus={restaurant.liveStatus}
          schedules={restaurant.eatzySchedules}
          timezone={restaurant.eatzyTimezone}
        />

        {/* Row 5: Ordering Info */}
        {restaurant.orderingEnabled && (
          <div className="flex items-center gap-2 text-sm" data-testid="ordering-info">
            <ShoppingCart className="w-3.5 h-3.5 text-kudoz flex-shrink-0" />
            <span className="font-medium text-kudoz">Order Online</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">
              {restaurant.deliveryAvailable && restaurant.pickupAvailable ? 'Delivery & Pickup' : 
               restaurant.deliveryAvailable ? 'Delivery' : 
               restaurant.pickupAvailable ? 'Pickup' : 'Available'}
            </span>
            {restaurant.minOrder && restaurant.minOrder > 0 && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">${restaurant.minOrder} min</span>
              </>
            )}
            {deliveryFee > 0 && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">${deliveryFee.toFixed(2)} delivery</span>
              </>
            )}
          </div>
        )}

        {/* Row 6: Description - Expandable */}
        {restaurant.description && (
          <div className="text-sm text-muted-foreground leading-relaxed">
            <p className={cn(!isDescriptionExpanded && "line-clamp-2")}>
              {restaurant.description}
            </p>
            {restaurant.description.length > 120 && (
              <button 
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                className="text-primary text-xs font-medium mt-1 hover:underline"
              >
                {isDescriptionExpanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        )}

        {/* Row 7: Social proof - Friends who visited + Friend score + Your rating */}
        {(socialData.friendsVisited.length > 0 || userRating !== null) && (
          <div className="flex items-center gap-3 py-1 flex-wrap">
            {socialData.friendsVisited.length > 0 && (
              <>
                <div className="flex -space-x-2">
                  {socialData.friendsVisited.slice(0, 5).map((friend) => (
                    <Avatar 
                      key={friend.id} 
                      className="w-7 h-7 border-2 border-background ring-2 ring-primary/20 cursor-pointer hover:ring-primary/50 transition-all"
                    >
                      <AvatarImage src={friend.avatar ?? undefined} alt={friend.name} />
                      <AvatarFallback className="text-xs">{friend.name[0]}</AvatarFallback>
                    </Avatar>
                  ))}
                  {socialData.friendsVisited.length > 5 && (
                    <div className="w-7 h-7 rounded-full border-2 border-background bg-muted flex items-center justify-center text-xs font-medium">
                      +{socialData.friendsVisited.length - 5}
                    </div>
                  )}
                </div>
                <span className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{socialData.friendsVisited.length} friends</span> visited
                </span>
                {socialData.friendScore !== null && (
                  <>
                    <span className="text-muted-foreground">·</span>
                    <div className="flex items-center gap-1" data-testid="friend-score">
                      <Users className="w-3.5 h-3.5 text-blue-500" />
                      <span className="font-medium text-blue-600 dark:text-blue-400">{socialData.friendScore.toFixed(1)}</span>
                      <span className="text-xs text-muted-foreground">avg</span>
                    </div>
                  </>
                )}
              </>
            )}
            {userRating !== null && userRating > 0 && (
              <>
                {socialData.friendsVisited.length > 0 && <span className="text-muted-foreground">·</span>}
                <div className="flex items-center gap-1" data-testid="your-rating">
                  <Heart className="w-3.5 h-3.5 text-pink-500 fill-pink-500" />
                  <span className="text-sm text-muted-foreground">You rated <span className="font-medium text-foreground">{userRating.toFixed(1)}</span></span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Friend Photos Gallery - TikTok-style vertical cards */}
        {friendPhotos.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Photos from Friends
              </h3>
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground">
                See All ({friendPhotos.length})
              </Button>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
              {friendPhotos.map((photo) => (
                <div
                  key={photo.id}
                  className="relative flex-shrink-0 cursor-pointer rounded-xl overflow-visible snap-start hover-elevate active-elevate-2"
                  onClick={() => setSelectedReview(photo)}
                >
                  <img
                    src={photo.url}
                    alt={`Photo by ${photo.friendName}`}
                    className="w-32 h-44 object-cover"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  
                  {/* Content overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Avatar className="w-6 h-6 border-2 border-white ring-1 ring-primary/50">
                        <AvatarImage src={photo.friendAvatar || undefined} />
                        <AvatarFallback className="text-xs">{photo.friendName[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs text-white font-medium truncate">{photo.friendName}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm text-white font-bold">{photo.rating.toFixed(1)}</span>
                    </div>
                    {/* Preview of review text */}
                    <p className="text-xs text-white/80 line-clamp-2 mt-1 leading-tight">
                      {photo.reviewText}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Double Kudoz Promotion Banner */}
        {socialData.doubleKudoz && (
          <div className="bg-kudoz/10 border border-kudoz/30 rounded-lg p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-kudoz/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-kudoz" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-kudoz text-sm">Double Kudoz Promotion!</div>
              <div className="text-xs text-muted-foreground">Earn 2x Kudoz on all orders this week</div>
            </div>
            <KudozDisplay amount={0} size="sm" />
          </div>
        )}

        {/* Action Buttons Row - Order priority for Tier 2 (only when not in order mode), Review priority for Tier 1 */}
        <div ref={actionButtonsRef} className="flex gap-2">
          {restaurant.orderingEnabled ? (
            <>
              {/* Hide Start Order button when already in order mode or cart has items */}
              {menuDisplayMode === 'browse' && cart.length === 0 && (
                <Button
                  variant="default"
                  size="default"
                  onClick={() => setIsEditServiceModalOpen(true)}
                  className="flex-1"
                  data-testid="button-start-order"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Start Order
                </Button>
              )}
              <Button
                variant="outline"
                size="default"
                onClick={() => setIsReviewModalOpen(true)}
                className={menuDisplayMode === 'order' || cart.length > 0 ? "flex-1" : "px-4"}
                data-testid="button-write-review"
              >
                <PenLine className="w-4 h-4 mr-2" />
                Review
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="default"
                size="default"
                onClick={() => setIsReviewModalOpen(true)}
                className="flex-1"
                data-testid="button-write-review"
              >
                <PenLine className="w-4 h-4 mr-2" />
                Write a Review
              </Button>
              <Button
                variant="outline"
                size="default"
                onClick={() => {
                  toast({
                    description: "Ask a friend feature coming soon!"
                  });
                }}
                className="px-4"
                data-testid="button-ask-friend"
              >
                <Users className="w-4 h-4 mr-2" />
                Ask
              </Button>
            </>
          )}
        </div>
        
        {/* Recent Reviews Preview */}
        {friendPhotos.length > 0 && (
          <div className="bg-muted/50 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Recent Reviews</span>
              <Button variant="ghost" size="sm" className="text-xs h-7">
                See all
              </Button>
            </div>
            <div className="space-y-2">
              {friendPhotos.slice(0, 2).map((review) => (
                <div 
                  key={review.id} 
                  className="flex items-start gap-2 p-2 bg-background rounded-md cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setSelectedReview(review)}
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={review.friendAvatar || undefined} />
                    <AvatarFallback>{review.friendName[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{review.friendName}</span>
                      <div className="flex items-center gap-0.5">
                        <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                        <span className="text-xs font-medium">{review.rating.toFixed(1)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">{review.reviewText}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cuisine Type Tags */}
        {cuisineTypes.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {cuisineTypes.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs" data-testid={`tag-${tag}`}>
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Order Availability Banner - show for Tier 2 restaurants when ordering is affected */}
      {restaurant?.orderingEnabled && orderAvailability.bannerType !== 'none' && (
        <div className="px-4">
          <OrderAvailabilityBanner 
            bannerType={orderAvailability.bannerType}
            bannerMessage={orderAvailability.bannerMessage}
            onScheduleOrder={handleScheduleOrder}
          />
        </div>
      )}

      {/* Your Past Orders - Quick Reorder */}
      {restaurantOrders.length > 0 && (
        <div className="px-4 py-3 space-y-2" data-testid="section-past-orders">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Your Past Orders</h3>
            <span className="text-xs text-muted-foreground">{restaurantOrders.length} order{restaurantOrders.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {restaurantOrders.map((order) => (
              <Card
                key={`past-${order.id}`}
                className="flex-shrink-0 w-[180px] hover-elevate active-elevate-2 cursor-pointer"
                onClick={() => {
                  setReorderModalData({
                    orderId: String(order.id),
                    restaurantId: restaurantId!,
                    eatzyClientId: restaurant?.eatzyClientId || '',
                    serviceType: (order.type || order.mode || 'takeout') as 'takeout' | 'delivery',
                    deliveryLat: order.address?.latitude ? Number(order.address.latitude) : undefined,
                    deliveryLng: order.address?.longitude ? Number(order.address.longitude) : undefined,
                    deliveryAddressId: order.address?.id ? Number(order.address.id) : undefined,
                  });
                }}
                data-testid={`past-order-${order.id}`}
              >
                <CardContent className="p-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{formatOrderDate(order.date)}</span>
                    <Badge variant="secondary" className="text-xs capitalize">{order.type || order.mode || 'takeout'}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">
                    {order.items?.map(i => i.name).slice(0, 3).join(', ')}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">{formatOrderTotal(order.total)}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs">
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Reorder
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Menu Search */}
      {menuItems.length > 0 && (
        <div className="px-4 py-2 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search menu..."
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              className="pl-9 pr-9"
              data-testid="input-menu-search"
            />
            {menuSearch && (
              <Button
                size="icon"
                variant="ghost"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setMenuSearch('')}
                data-testid="button-clear-search"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          {menuSearch.trim() && (
            <p className="text-xs text-muted-foreground mt-1.5">
              {filteredMenu.length === 0
                ? `No results for "${menuSearch.trim()}"`
                : `${filteredMenu.length} result${filteredMenu.length === 1 ? '' : 's'} for "${menuSearch.trim()}"`}
            </p>
          )}
        </div>
      )}

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={(cat) => { setActiveCategory(cat); setMenuSearch(''); }}>
        <div className="border-b sticky top-0 bg-background z-10 overflow-x-auto">
          <TabsList className={cn("inline-flex w-max min-w-full justify-start h-auto p-0 bg-transparent px-4", menuSearch.trim() && "opacity-50")}>
            {categories.map((category) => (
              <TabsTrigger
                key={category}
                value={category}
                className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3 whitespace-nowrap flex-shrink-0"
                data-testid={`tab-${category.toLowerCase()}`}
              >
                {category}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Category Description Banner - shows for individual category views (not All or Friend Favorites) */}
        {activeCategory !== 'All' && activeCategory !== 'Friend Favorites' && categoryDescriptions.get(activeCategory) && (
          <div className="px-4 py-3 bg-muted/50 border-b">
            <p className="text-sm text-muted-foreground">{categoryDescriptions.get(activeCategory)}</p>
          </div>
        )}

        <div className={cn(
          "p-4 space-y-3",
          // Order mode with cart: clear the tall cart footer
          cartItemCount > 0 && restaurant?.orderingEnabled && "pb-60",
          // FAB visible for Tier 2 (browse, empty cart, full-width CTA not showing yet): clear FAB at bottom-6
          restaurant?.orderingEnabled && menuDisplayMode === 'browse' && cart.length === 0 && !showFloatingCTA && "pb-24",
          // Full-width Start Your Order button visible (FAB hidden at this point): button sits at bottom-24 + ~48px height
          showFloatingCTA && restaurant?.orderingEnabled && menuDisplayMode === 'browse' && cart.length === 0 && "pb-36",
          // FAB always visible for Tier 1 (Write Review): clear FAB at bottom-6
          !restaurant?.orderingEnabled && "pb-24"
        )}>
            {/* Menu unavailable state */}
            {(browseMenuError || (isMenuEmpty && restaurant?.orderingEnabled && menuDisplayMode === 'browse')) && filteredMenu.length === 0 && (
              <Card className="p-8 text-center">
                <CardContent className="flex flex-col items-center justify-center space-y-4 p-0">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                    <UtensilsCrossed className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold text-lg">Menu Temporarily Unavailable</h3>
                    <p className="text-sm text-muted-foreground max-w-sm">
                      We're having trouble loading the menu for this restaurant. 
                      Please try again later or contact the restaurant directly.
                    </p>
                  </div>
                  {restaurant?.phone && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={`tel:${restaurant.phone}`}>
                        Call Restaurant
                      </a>
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
            {/* Render menu items - grouped by category for "All" view, flat for other views */}
            {activeCategory === 'All' && groupedMenuItems ? (
              // Grouped view with sticky category headers
              groupedMenuItems.map((group) => (
                <div key={group.name} className="mb-6 last:mb-0">
                  {/* Sticky Category Header */}
                  <div className="sticky top-[49px] bg-background z-[5] -mx-4 px-4 py-2 border-b mb-3">
                    <h2 className="font-semibold text-lg">{group.name}</h2>
                    {group.description && (
                      <p className="text-sm text-muted-foreground mt-0.5">{group.description}</p>
                    )}
                  </div>
                  
                  {/* Category Items */}
                  <div className="space-y-3">
                    {(() => {
                      const sectionHasImages = group.items.some(i => i.image);
                      return group.items.map((item) => {
                      const cartQuantity = getItemCartQuantity(item.id);
                      
                      return (
                        <Card
                          key={item.id}
                          className="overflow-hidden hover-elevate cursor-pointer"
                          data-testid={`menu-item-${item.id}`}
                          onClick={() => handleItemClick(item)}
                        >
                          <CardContent className="p-3">
                            <div className="flex gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <h3 className="font-semibold">{item.name}</h3>
                                  {item.popular && <Badge variant="secondary" className="text-xs">Popular</Badge>}
                                  {item.kudozPromotion && (
                                    <Badge variant="secondary" className="text-xs bg-kudoz/15 text-kudoz border-kudoz/30 hover:bg-kudoz/20 flex items-center gap-1" data-testid={`kudoz-promo-${item.id}`}>
                                      <KudozIcon size="xs" />
                                      {item.kudozPromotion.type === 'double' && '2x'}
                                      {item.kudozPromotion.type === 'bonus' && item.kudozPromotion.amount && `+${item.kudozPromotion.amount}`}
                                      {item.kudozPromotion.type === 'daily-special' && item.kudozPromotion.amount && `+${item.kudozPromotion.amount}`}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                                {(personalItemMap.has(item.name.toLowerCase()) || friendItemMap.has(item.name.toLowerCase())) && (() => {
                                  const _key = item.name.toLowerCase();
                                  const _personal = personalItemMap.get(_key);
                                  const _friend = !_personal ? friendItemMap.get(_key) : undefined;
                                  const _personalLabel = _personal?.rating === 3 ? 'You loved this' : _personal?.rating === 2 ? "You've tried this" : 'Not your fave';
                                  return (
                                    <div className="flex items-center gap-1.5 mt-1">
                                      {_personal && (
                                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted/60 rounded-full px-2 py-0.5 whitespace-nowrap">
                                          {_personal.photoUrl && <img src={_personal.photoUrl} alt="" className="w-4 h-4 rounded-full object-cover flex-shrink-0" />}
                                          {_personal.rating === 3 ? <Heart className="w-3 h-3 text-red-500 flex-shrink-0" /> : _personal.rating === 2 ? <Meh className="w-3 h-3 text-yellow-500 flex-shrink-0" /> : <ThumbsDown className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
                                          <span>{_personalLabel}</span>
                                        </span>
                                      )}
                                      {_friend && (
                                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted/60 rounded-full px-2 py-0.5 whitespace-nowrap">
                                          <span className="flex items-center -space-x-1 flex-shrink-0">
                                            {_friend.friendAvatars.slice(0, 2).map((av, i) => (
                                              <Avatar key={i} className="w-4 h-4 border border-background" style={{ zIndex: 2 - i }}>
                                                <AvatarImage src={av} />
                                                <AvatarFallback className="text-[8px]">F</AvatarFallback>
                                              </Avatar>
                                            ))}
                                          </span>
                                          <span>{_friend.count} {_friend.count === 1 ? 'friend' : 'friends'} {_friend.rating === 3 ? 'loved this' : 'tried this'}</span>
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                                {item.friendRecommendations && item.friendRecommendations > 0 && (
                                  <div className="flex items-center gap-2 mt-1">
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <Users className="w-3 h-3" />
                                      <span className="font-medium" data-testid={`recommendations-${item.id}`}>{item.friendRecommendations} friends recommend</span>
                                    </div>
                                    {item.friendPhotos && item.friendPhotos.length > 0 && (
                                      <div className="flex -space-x-2">
                                        {item.friendPhotos.slice(0, 3).map((photo, idx) => (
                                          <Avatar key={idx} className="w-5 h-5 border-2 border-background">
                                            <AvatarImage src={photo.friendAvatar || undefined} />
                                            <AvatarFallback className="text-xs">{photo.friendName[0]}</AvatarFallback>
                                          </Avatar>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="flex items-center justify-between mt-2">
                                  <p className="font-semibold">{getItemPriceDisplay(item)}</p>
                                  {restaurant?.orderingEnabled && menuDisplayMode === 'order' && (
                                    cartQuantity > 0 ? (
                                      <div className="text-sm text-muted-foreground font-medium" data-testid={`quantity-${item.id}`}>{cartQuantity} in cart</div>
                                    ) : (
                                      <Button variant="default" size="sm" disabled={isOrderUnavailable} onClick={(e) => { e.stopPropagation(); handleItemClick(item); }} data-testid={`button-add-${item.id}`}>
                                        <Plus className="w-4 h-4 mr-1" />Add
                                      </Button>
                                    )
                                  )}
                                </div>
                              </div>
                              {sectionHasImages && (
                                item.image
                                  ? <img src={item.image} alt={item.name} className="w-24 h-24 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                                  : <div className="w-24 h-24 flex-shrink-0" />
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    });
                    })()}
                  </div>
                </div>
              ))
            ) : (
              // Flat view for Friend Favorites and individual categories
              (() => {
                const flatHasImages = filteredMenu.some(i => i.image);
                return filteredMenu.map((item) => {
                const cartQuantity = getItemCartQuantity(item.id);
                
                return (
                  <Card
                    key={item.id}
                    className="overflow-hidden hover-elevate cursor-pointer"
                    data-testid={`menu-item-${item.id}`}
                    onClick={() => handleItemClick(item)}
                  >
                    <CardContent className="p-3">
                      <div className="flex gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold">{item.name}</h3>
                            {item.popular && <Badge variant="secondary" className="text-xs">Popular</Badge>}
                            {item.kudozPromotion && (
                              <Badge variant="secondary" className="text-xs bg-kudoz/15 text-kudoz border-kudoz/30 hover:bg-kudoz/20 flex items-center gap-1" data-testid={`kudoz-promo-${item.id}`}>
                                <KudozIcon size="xs" />
                                {item.kudozPromotion.type === 'double' && '2x'}
                                {item.kudozPromotion.type === 'bonus' && item.kudozPromotion.amount && `+${item.kudozPromotion.amount}`}
                                {item.kudozPromotion.type === 'daily-special' && item.kudozPromotion.amount && `+${item.kudozPromotion.amount}`}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                          {(personalItemMap.has(item.name.toLowerCase()) || friendItemMap.has(item.name.toLowerCase())) && (() => {
                            const _key = item.name.toLowerCase();
                            const _personal = personalItemMap.get(_key);
                            const _friend = !_personal ? friendItemMap.get(_key) : undefined;
                            const _personalLabel = _personal?.rating === 3 ? 'You loved this' : _personal?.rating === 2 ? "You've tried this" : 'Not your fave';
                            return (
                              <div className="flex items-center gap-1.5 mt-1">
                                {_personal && (
                                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted/60 rounded-full px-2 py-0.5 whitespace-nowrap">
                                    {_personal.photoUrl && <img src={_personal.photoUrl} alt="" className="w-4 h-4 rounded-full object-cover flex-shrink-0" />}
                                    {_personal.rating === 3 ? <Heart className="w-3 h-3 text-red-500 flex-shrink-0" /> : _personal.rating === 2 ? <Meh className="w-3 h-3 text-yellow-500 flex-shrink-0" /> : <ThumbsDown className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
                                    <span>{_personalLabel}</span>
                                  </span>
                                )}
                                {_friend && (
                                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted/60 rounded-full px-2 py-0.5 whitespace-nowrap">
                                    <span className="flex items-center -space-x-1 flex-shrink-0">
                                      {_friend.friendAvatars.slice(0, 2).map((av, i) => (
                                        <Avatar key={i} className="w-4 h-4 border border-background" style={{ zIndex: 2 - i }}>
                                          <AvatarImage src={av} />
                                          <AvatarFallback className="text-[8px]">F</AvatarFallback>
                                        </Avatar>
                                      ))}
                                    </span>
                                    <span>{_friend.count} {_friend.count === 1 ? 'friend' : 'friends'} {_friend.rating === 3 ? 'loved this' : 'tried this'}</span>
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          {item.friendRecommendations && item.friendRecommendations > 0 && (
                            <div className="flex items-center gap-2 mt-1">
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Users className="w-3 h-3" />
                                <span className="font-medium" data-testid={`recommendations-${item.id}`}>{item.friendRecommendations} friends recommend</span>
                              </div>
                              {item.friendPhotos && item.friendPhotos.length > 0 && (
                                <div className="flex -space-x-2">
                                  {item.friendPhotos.slice(0, 3).map((photo, idx) => (
                                    <Avatar key={idx} className="w-5 h-5 border-2 border-background">
                                      <AvatarImage src={photo.friendAvatar || undefined} />
                                      <AvatarFallback className="text-xs">{photo.friendName[0]}</AvatarFallback>
                                    </Avatar>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-2">
                            <p className="font-semibold">{getItemPriceDisplay(item)}</p>
                            {restaurant?.orderingEnabled && menuDisplayMode === 'order' && (
                              cartQuantity > 0 ? (
                                <div className="text-sm text-muted-foreground font-medium" data-testid={`quantity-${item.id}`}>{cartQuantity} in cart</div>
                              ) : (
                                <Button variant="default" size="sm" disabled={isOrderUnavailable} onClick={(e) => { e.stopPropagation(); handleItemClick(item); }} data-testid={`button-add-${item.id}`}>
                                  <Plus className="w-4 h-4 mr-1" />Add
                                </Button>
                              )
                            )}
                          </div>
                        </div>
                        {flatHasImages && (
                          item.image
                            ? <img src={item.image} alt={item.name} className="w-24 h-24 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                            : <div className="w-24 h-24 flex-shrink-0" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
                });
              })()
            )}
        </div>
      </Tabs>
      
      {/* Similar Restaurants Section */}
      {(isSimilarLoading || similarRestaurants.length > 0) && (
        <div className="p-4 space-y-3 border-t">
          <h3 className="font-semibold flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            Similar Restaurants
          </h3>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory">
            {isSimilarLoading ? (
              [1, 2, 3].map(i => (
                <div key={i} className="flex-shrink-0 w-44 snap-start">
                  <div className="rounded-xl overflow-hidden bg-card border">
                    <div className="h-24 bg-muted animate-pulse" />
                    <div className="p-2 space-y-1">
                      <div className="h-4 w-28 bg-muted rounded animate-pulse" />
                      <div className="h-3 w-16 bg-muted rounded animate-pulse" />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              similarRestaurants.map((similar) => {
                const imageUrl = similar.logoImageUrl || similar.primaryImageUrl;
                const rating = similar.overallRating ? parseFloat(similar.overallRating) : 0;
                const cuisine = similar.brandCategoryName || similar.category || '';
                return (
                  <div
                    key={similar.id}
                    onClick={() => navigate(`/restaurant/${similar.id}`)}
                    className="flex-shrink-0 w-44 snap-start cursor-pointer hover-elevate active-elevate-2"
                  >
                    <div className="rounded-xl overflow-hidden bg-card border shadow-sm">
                      <div className="relative h-24">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={similar.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            <UtensilsCrossed className="w-8 h-8 text-muted-foreground/40" />
                          </div>
                        )}
                        {rating > 0 && (
                          <>
                            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/60 to-transparent" />
                            <div className="absolute bottom-2 left-2 flex items-center gap-1 text-white text-xs">
                              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                              <span className="font-bold">{rating.toFixed(1)}</span>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="p-2">
                        <h4 className="font-medium text-sm truncate">{similar.name}</h4>
                        {cuisine && (
                          <span className="text-xs text-muted-foreground">{cuisine}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Cart Footer - Fixed at bottom (only for ordering-enabled restaurants) */}
      {cartItemCount > 0 && restaurant?.orderingEnabled && (
        <div className="lg:hidden fixed bottom-safe-area-16 md:bottom-0 left-0 right-0 border-t bg-background shadow-[0_-4px_6px_-1px_rgb(0_0_0_/_0.1)] z-50">
          {/* Row 1: Compact Order Summary - Always Visible */}
          <div className="px-4 py-2 border-b flex items-center gap-2">
            <Badge variant="outline" className="text-xs flex-shrink-0">
              {urlParams.serviceType === 'delivery' ? (
                <><Truck className="w-3 h-3 mr-1" />Delivery</>
              ) : (
                <><Package className="w-3 h-3 mr-1" />Takeout</>
              )}
            </Badge>
            <div className="flex items-center gap-1.5 flex-1 min-w-0 text-sm text-muted-foreground">
              <span className="truncate">{urlParams.location?.substring(0, 25)}{urlParams.location && urlParams.location.length > 25 ? '...' : ''}</span>
              <span>•</span>
              <span className="flex-shrink-0">
                {urlParams.timing === 'asap' ? 'ASAP' : formatScheduledTimeDisplay(urlParams.scheduledTime, restaurant?.eatzyTimezone)}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsEditServiceModalOpen(true)}
                data-testid="button-edit-service"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsCartFooterExpanded(!isCartFooterExpanded)}
                data-testid="button-expand-cart-footer"
              >
                <ChevronDown className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  isCartFooterExpanded && "rotate-180"
                )} />
              </Button>
            </div>
          </div>

          {/* Expanded Details - Collapsible */}
          {isCartFooterExpanded && (
            <div className="px-4 py-2 border-b bg-muted/30 space-y-2">
              {/* Full Location */}
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">
                    {urlParams.serviceType === 'delivery' ? 'Delivering To' : 'Pickup Location'}
                  </div>
                  <div className="font-medium break-words">{urlParams.location}</div>
                </div>
              </div>
              {/* Timing Details */}
              <div className="flex items-center gap-2 text-sm">
                <CalendarClock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div>
                  {urlParams.timing === 'asap' && !isRestaurantClosed ? (
                    <span>ASAP ({deliveryTime})</span>
                  ) : urlParams.timing === 'scheduled' && urlParams.scheduledTime ? (
                    <span>Scheduled: {formatScheduledTimeDisplay(urlParams.scheduledTime, restaurant?.eatzyTimezone)}</span>
                  ) : (
                    <span className="text-amber-600 dark:text-amber-400">Schedule required</span>
                  )}
                </div>
              </div>
              {/* Kudoz Earned Details */}
              <div className="flex items-center justify-between py-1.5 px-3 bg-kudoz/10 rounded-md border border-kudoz/20">
                <div className="flex items-center gap-2">
                  <KudozIcon size="sm" />
                  <span className="text-sm font-medium">Kudoz you'll earn</span>
                </div>
                <span className="text-base font-bold text-kudoz tabular-nums" data-testid="floating-cart-kudoz">
                  +{Math.floor(cartTotal * 7)}
                </span>
              </div>
            </div>
          )}

          {/* Row 2: View Cart Button with integrated Kudoz */}
          <div className="px-4 py-3">
            <Button
              className="w-full flex items-center justify-between gap-2"
              size="lg"
              onClick={() => setCartDrawerOpen(true)}
              data-testid="button-view-cart"
            >
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                <span>{cartItemCount} items</span>
                <span className="text-primary-foreground/70">•</span>
                <div className="flex items-center gap-1 text-primary-foreground/90">
                  <KudozIcon size="xs" className="opacity-80" />
                  <span className="text-sm">+{Math.floor(cartTotal * 7)}</span>
                </div>
              </div>
              <span className="font-bold">${cartTotal.toFixed(2)}</span>
            </Button>
          </div>
        </div>
      )}

      {/* Item Detail Modal */}
      {selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          isOpen={true}
          onClose={() => {
            setSelectedItem(null);
            setEditingCartIndex(null);
          }}
          onAddToCart={handleAddToCartFromModal}
          orderingEnabled={restaurant?.orderingEnabled || false}
          isInOrderMode={isOrderingMode}
          onStartOrder={() => { setSelectedItem(null); setEditingCartIndex(null); setShowOrderInitiationModal(true); }}
          restaurantId={restaurantId}
          isEditMode={editingCartIndex !== null}
          initialQuantity={editingCartIndex !== null ? cart[editingCartIndex]?.quantity : undefined}
          initialSelections={editingCartIndex !== null ? cart[editingCartIndex]?.selections : undefined}
          initialSpecialInstructions={editingCartIndex !== null ? (cart[editingCartIndex]?.specialInstructions ?? '') : undefined}
          personalReview={personalItemMap.get(selectedItem.name.toLowerCase())}
        />
      )}

      {/* Cart Drawer */}
      <Sheet open={cartDrawerOpen} onOpenChange={(open) => {
        console.log('[Cart] Cart drawer open changed to:', open, 'Cart has', cart.length, 'items');
        setCartDrawerOpen(open);
      }}>
        <SheetContent side="bottom" className="h-[80svh] flex flex-col p-0">
          <div className="px-6 pt-6 pb-2 border-b">
            <SheetHeader className="p-0">
              <SheetTitle>Your Cart</SheetTitle>
            </SheetHeader>
          </div>
          <ScrollArea className="flex-1 px-6">
            <div className="space-y-3 py-4">
              {isOrderTimeExpired && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-destructive">Order time expired</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {orderAvailability.bannerMessage || 'Your selected time has passed.'}
                        {' '}Please choose a new time to continue.
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      setCartDrawerOpen(false);
                      setIsEditServiceModalOpen(true);
                    }}
                    data-testid="button-update-time"
                  >
                    <CalendarClock className="w-4 h-4 mr-1.5" />
                    Update order time
                  </Button>
                </div>
              )}
              {cart.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Your cart is empty</p>
                  <p className="text-xs mt-1">Add items from the menu to get started</p>
                </div>
              ) : cart.map((cartItem, index) => (
                <Card key={index} data-testid={`cart-item-${index}`}>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <h4 className="font-semibold">{cartItem.item.name}</h4>
                        {(Object.keys(cartItem.selections).length > 0 || cartItem.specialInstructions) && (
                          <div className="text-sm text-muted-foreground mt-1">
                            {Object.entries(cartItem.selections).map(([groupId, optionIds]) => {
                              const group = cartItem.item.modifiers?.find(g => g.id === groupId);
                              if (!group) return null;
                              const selectedOptions = group.options.filter(opt => optionIds.includes(opt.id));
                              return (
                                <div key={groupId}>
                                  {selectedOptions.map(opt => opt.name).join(', ')}
                                </div>
                              );
                            })}
                            {cartItem.specialInstructions && (
                              <div className="italic text-xs mt-1">
                                "{cartItem.specialInstructions}"
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => removeFromCart(index)}
                              data-testid={`button-cart-minus-${index}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="font-medium w-6 text-center" data-testid={`cart-quantity-${index}`}>
                              {cartItem.quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              disabled={isOrderTimeExpired}
                              onClick={() => addOneToCart(index)}
                              data-testid={`button-cart-plus-${index}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setEditingCartIndex(index);
                              setSelectedItem(cartItem.item);
                            }}
                            data-testid={`button-edit-${index}`}
                          >
                            <PenLine className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setCart(cart.filter((_, i) => i !== index));
                            }}
                            data-testid={`button-remove-${index}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">${(cartItem.totalPrice * cartItem.quantity).toFixed(2)}</p>
                        <p className="text-sm text-muted-foreground">${cartItem.totalPrice.toFixed(2)} each</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
          <div className="border-t bg-background">
            {/* Collapsible Service Context Bar */}
            <div className="px-6 py-2 border-b">
              {/* Collapsed View Header - Single compact line */}
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setCartContextExpanded(!cartContextExpanded)}
                data-testid="cart-context-toggle"
              >
                <div className="flex items-center gap-2 text-sm flex-1 min-w-0">
                  {urlParams.serviceType === 'delivery' ? (
                    <Truck className="w-4 h-4 text-kudoz flex-shrink-0" />
                  ) : (
                    <Package className="w-4 h-4 text-kudoz flex-shrink-0" />
                  )}
                  <span className="font-medium capitalize">{urlParams.serviceType}</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground truncate max-w-[120px]">
                    {urlParams.location?.split(',')[0] || 'Address'}
                  </span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-muted-foreground truncate">
                    {urlParams.timing === 'asap' && !isRestaurantClosed 
                      ? `ASAP (${deliveryTime})` 
                      : urlParams.scheduledTime 
                        ? formatScheduledTimeDisplay(urlParams.scheduledTime, restaurant?.eatzyTimezone)
                        : 'Schedule required'}
                  </span>
                </div>
                <ChevronDown className={cn(
                  "w-4 h-4 text-muted-foreground transition-transform flex-shrink-0",
                  cartContextExpanded && "rotate-180"
                )} />
              </div>

              {/* Expanded View - Full details */}
              {cartContextExpanded && (
                <div className="pt-3 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{urlParams.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <CalendarClock className="w-3 h-3 flex-shrink-0" />
                    {urlParams.timing === 'asap' && !isRestaurantClosed ? (
                      <span>ASAP ({deliveryTime})</span>
                    ) : urlParams.timing === 'scheduled' && urlParams.scheduledTime ? (
                      <span>Scheduled: {formatScheduledTimeDisplay(urlParams.scheduledTime, restaurant?.eatzyTimezone)}</span>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-400">Schedule required</span>
                    )}
                  </div>
                  {urlParams.serviceType === 'delivery' && deliveryFee > 0 && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Truck className="w-3 h-3 flex-shrink-0" />
                      <span>Delivery fee: ${deliveryFee.toFixed(2)}</span>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCartDrawerOpen(false);
                      setIsEditServiceModalOpen(true);
                    }}
                    data-testid="button-edit-service-drawer"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit order details
                  </Button>
                </div>
              )}
            </div>

            {/* Checkout Section - Streamlined */}
            <div className="px-6 py-3 space-y-2">
              {/* Total and Kudoz on same line */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Total</span>
                  <span className="text-xl font-bold" data-testid="cart-total">${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-kudoz">
                  <KudozIcon size="sm" />
                  <span className="font-medium" data-testid="cart-kudoz-earned">+{Math.floor(cartTotal * 7)}</span>
                </div>
              </div>

              {/* Minimum order warning */}
              {belowMinOrder > 0 && (
                <div className="flex items-center gap-2 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 px-3 py-2" data-testid="min-order-warning-drawer">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    Minimum order ${restaurant!.minOrder!.toFixed(2)} — add{' '}
                    <span className="font-semibold">${belowMinOrder.toFixed(2)}</span> more to proceed
                  </p>
                </div>
              )}
              
              {/* Checkout button with Add More as text link */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={() => setCartDrawerOpen(false)}
                  data-testid="button-add-more-items"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add more
                </Button>
                <Button
                  className="flex-1"
                  size="lg"
                  disabled={isOrderTimeExpired || belowMinOrder > 0}
                  onClick={() => {
                    // Save order config to localStorage for checkout page
                    if (urlParams.serviceType) {
                      localStorage.setItem('eatzy-order-config', JSON.stringify({
                        serviceType: urlParams.serviceType,
                        timing: urlParams.timing || 'asap',
                        scheduledTime: urlParams.scheduledTime || null,
                        addressId: urlParams.addressId || orderConfig?.addressId || null,
                      }));
                    }

                    // If not authenticated, show sign-in / guest checkout sheet
                    if (!isAuthenticated) {
                      setCartDrawerOpen(false);
                      setGuestCheckoutSheetOpen(true);
                      return;
                    }
                    
                    // Pre-checkout validation: check for required data
                    const serviceType = urlParams.serviceType || orderConfig?.serviceType || 'delivery';
                    const hasPhone = phones.length > 0;
                    const hasAddress = serviceType === 'delivery' 
                      ? !!(urlParams.addressId || orderConfig?.addressId || primaryAddress)
                      : true;
                    
                    if (!hasPhone && !isProfileLoading) {
                      setCartDrawerOpen(false);
                      setPreCheckoutNewPhone('');
                      setShowPreCheckoutModal(true);
                      return;
                    }
                    
                    if (!hasAddress) {
                      toast({
                        title: 'Delivery address required',
                        description: 'Please add a delivery address before checking out.',
                        variant: 'destructive',
                      });
                      return;
                    }
                    
                    navigate('/checkout');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  data-testid="button-checkout"
                >
                  {belowMinOrder > 0
                    ? `Add $${belowMinOrder.toFixed(2)} more`
                    : 'Checkout'}
                </Button>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Review Detail Modal */}
      <Dialog open={selectedReview !== null} onOpenChange={(open) => !open && setSelectedReview(null)}>
        <DialogContent className="max-w-lg" data-testid="review-modal">
          {selectedReview && (
            <div className="space-y-4">
              <DialogHeader>
                <DialogTitle className="sr-only">Friend's Review</DialogTitle>
              </DialogHeader>
              
              {/* Friend Info */}
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={selectedReview.friendAvatar || undefined} />
                  <AvatarFallback>{selectedReview.friendName[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="font-semibold">{selectedReview.friendName}</h3>
                  <p className="text-sm text-muted-foreground">
                    {formatDistanceToNow(new Date(selectedReview.visitDate), { addSuffix: true })}
                  </p>
                </div>
                <div className="flex items-center gap-1 bg-primary/10 px-3 py-1.5 rounded-full">
                  <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                  <span className="font-bold text-lg">{selectedReview.rating}</span>
                </div>
              </div>

              {/* Review Photo */}
              <div className="relative w-full h-64 rounded-lg overflow-hidden">
                <img
                  src={selectedReview.url}
                  alt={`Review photo by ${selectedReview.friendName}`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Review Text */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Review</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {selectedReview.reviewText}
                </p>
              </div>

              {/* Restaurant Info */}
              <div className="pt-3 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Reviewed</span>
                  <span className="font-medium">{restaurant.name}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Floating FAB - Order Now for Tier 2 (hidden when cart has items), Write Review for Tier 1 */}
      {(restaurant.orderingEnabled ? (cart.length === 0 && menuDisplayMode === 'browse' && !showFloatingCTA) : true) && (
        <div className="lg:hidden fixed bottom-6 right-4 z-50 fm-spring-pop">
          {restaurant.orderingEnabled ? (
            <Button
              size="lg"
              onClick={() => setIsEditServiceModalOpen(true)}
              className="rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-shadow bg-primary hover:bg-primary/90"
              data-testid="fab-order-now"
            >
              <ShoppingCart className="w-6 h-6" />
            </Button>
          ) : (
            <Button
              size="lg"
              onClick={() => setIsReviewModalOpen(true)}
              className="rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-shadow bg-primary hover:bg-primary/90"
              data-testid="fab-write-review"
            >
              <Camera className="w-6 h-6" />
            </Button>
          )}
        </div>
      )}

      {/* Create Review Modal */}
      <CreateReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        restaurantId={restaurantId}
        restaurantName={restaurant.name}
      />

      {/* Edit Service Settings Modal - passes current values for editing */}
      <OrderInitiationModal
        isOpen={isEditServiceModalOpen}
        onClose={() => setIsEditServiceModalOpen(false)}
        onProceed={handleServiceSettingsUpdate}
        defaultLocation={urlParams.location}
        mode={restaurant?.orderingEnabled ? 'order' : 'discovery'}
        restaurantId={restaurant?.orderingEnabled ? restaurantId : undefined}
        isRestaurantOpen={restaurant?.liveStatus?.open}
        initialServiceType={orderConfig?.serviceType || urlParams.serviceType}
        initialTiming={orderConfig?.timing || (urlParams.timing as 'asap' | 'scheduled')}
        initialScheduledTime={urlParams.scheduledTime || undefined}
        initialAddressId={urlParams.addressId?.toString() || orderConfig?.addressId?.toString()}
        hoursJson={restaurant?.hoursJson as any}
        timezone={restaurant?.eatzyTimezone}
        sessionLocationName={sessionLocationName || undefined}
        sessionCoordinates={sessionCoords}
        onSessionLocationChange={setSessionLocation}
      />

      {/* Order Initiation Modal for starting order / scheduling */}
      <OrderInitiationModal
        isOpen={showOrderInitiationModal}
        onClose={() => setShowOrderInitiationModal(false)}
        onProceed={handleServiceSettingsUpdate}
        defaultLocation={urlParams.location}
        mode="order"
        restaurantId={restaurantId}
        defaultScheduledTime={restaurant?.liveStatus?.open === false && nextOpenAt ? new Date(nextOpenAt * 1000) : undefined}
        isRestaurantOpen={restaurant?.liveStatus?.open}
        initialServiceType={orderConfig?.serviceType || urlParams.serviceType}
        initialTiming={orderConfig?.timing || (urlParams.timing as 'asap' | 'scheduled')}
        initialScheduledTime={orderConfig?.scheduledStart 
          ? new Date(orderConfig.scheduledStart * 1000).toISOString() 
          : urlParams.scheduledTime}
        initialAddressId={urlParams.addressId?.toString() || orderConfig?.addressId?.toString()}
        hoursJson={restaurant?.hoursJson as any}
        timezone={restaurant?.eatzyTimezone}
        sessionLocationName={sessionLocationName || undefined}
        sessionCoordinates={sessionCoords}
        onSessionLocationChange={setSessionLocation}
      />
      
      {/* Context Change Confirmation Dialog */}
      <AlertDialog open={showContextChangeDialog} onOpenChange={setShowContextChangeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Order Settings?</AlertDialogTitle>
            <AlertDialogDescription>
              Changing your order settings will clear your current cart ({cart.length} {cart.length === 1 ? 'item' : 'items'}). 
              This is because menu items and pricing may differ based on your order type and timing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelContextChange}>
              Keep Current Settings
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmContextChange}>
              Clear Cart & Update
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pre-Checkout Validation Modal - collects missing required info before navigating to checkout */}
      <Dialog open={showPreCheckoutModal} onOpenChange={(open) => {
        setShowPreCheckoutModal(open);
        if (!open) {
          setPreCheckoutNewPhone('');
          setPreCheckoutSubmittingPhone(false);
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="modal-pre-checkout">
          <DialogHeader>
            <DialogTitle>Almost Ready</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              A phone number is needed so the restaurant or driver can contact you about your order.
            </p>
            <div className="space-y-2">
              <Label htmlFor="pre-checkout-phone">Phone Number</Label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 border rounded-lg bg-muted text-sm text-muted-foreground">
                  +1
                </div>
                <Input
                  id="pre-checkout-phone"
                  type="tel"
                  value={preCheckoutNewPhone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setPreCheckoutNewPhone(digits);
                  }}
                  placeholder="(555) 123-4567"
                  data-testid="input-pre-checkout-phone"
                  autoFocus
                />
              </div>
              {preCheckoutNewPhone.length > 0 && preCheckoutNewPhone.length < 10 && (
                <p className="text-xs text-muted-foreground">Enter a 10-digit phone number</p>
              )}
            </div>
            <Button
              className="w-full"
              disabled={preCheckoutNewPhone.length < 10 || preCheckoutSubmittingPhone}
              onClick={async () => {
                setPreCheckoutSubmittingPhone(true);
                try {
                  const res = await apiRequest('POST', '/api/user/phones', {
                    phoneNumber: preCheckoutNewPhone,
                    countryCode: '+1',
                    isPrimary: true,
                  });
                  const phoneData = await res.json();
                  if (!phoneData.phone) {
                    throw new Error(phoneData.error || 'Failed to add phone number');
                  }
                  await refreshPhones();
                  toast({ description: 'Phone number added' });
                  
                  const svcType = urlParams.serviceType || orderConfig?.serviceType || 'delivery';
                  const needsAddress = svcType === 'delivery' 
                    && !(urlParams.addressId || orderConfig?.addressId || primaryAddress);
                  
                  setShowPreCheckoutModal(false);
                  setPreCheckoutNewPhone('');
                  
                  if (needsAddress) {
                    toast({
                      title: 'Delivery address required',
                      description: 'Please add a delivery address before checking out.',
                      variant: 'destructive',
                    });
                    return;
                  }
                  
                  navigate('/checkout');
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                } catch (err: any) {
                  toast({
                    title: 'Could not add phone',
                    description: err.message || 'Please try again',
                    variant: 'destructive',
                  });
                } finally {
                  setPreCheckoutSubmittingPhone(false);
                }
              }}
              data-testid="button-pre-checkout-save-phone"
            >
              {preCheckoutSubmittingPhone ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save & Continue to Checkout
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reorder Validation Modal */}
      {reorderModalData && (
        <ReorderValidationModal
          isOpen={!!reorderModalData}
          onClose={() => setReorderModalData(null)}
          orderId={reorderModalData.orderId}
          restaurantId={reorderModalData.restaurantId}
          eatzyClientId={reorderModalData.eatzyClientId}
          serviceType={reorderModalData.serviceType}
          deliveryLat={reorderModalData.deliveryLat}
          deliveryLng={reorderModalData.deliveryLng}
          deliveryAddressId={reorderModalData.deliveryAddressId}
          onSuccess={(info) => {
            setReorderModalData(null);
            if (info.action !== 'modify') {
              navigate('/checkout');
            }
            // 'modify': user stays on the restaurant page; cart was written to
            // localStorage by handleAddToCart and the page will enter order mode.
          }}
        />
      )}

      {/* Floating Start Your Order Button - Only shows when action buttons scroll out of view and not in order mode */}
      {restaurant?.orderingEnabled && menuDisplayMode === 'browse' && cart.length === 0 && orderAvailability.isAvailable && showFloatingCTA && (
        <div className="lg:hidden fixed bottom-24 left-4 right-4 z-50 fm-slide-up">
          <Button
            size="lg"
            onClick={handleStartOrder}
            className="w-full shadow-lg"
            data-testid="button-floating-start-order"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            Start Your Order
          </Button>
        </div>
      )}
      </div>
      {/* Right: Desktop cart sidebar */}
      {restaurant?.orderingEnabled && (
        <div className="hidden lg:flex flex-col border-l bg-background overflow-y-auto">
          <div className="px-4 py-4 border-b flex-shrink-0">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              <h2 className="font-semibold text-lg">Your Cart</h2>
              {cartItemCount > 0 && (
                <span className="ml-auto text-sm text-muted-foreground">{cartItemCount} {cartItemCount === 1 ? 'item' : 'items'}</span>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {isOrderTimeExpired && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 space-y-2 mb-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-destructive">Order time expired</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {orderAvailability.bannerMessage || 'Your selected time has passed.'}
                      {' '}Please choose a new time to continue.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setIsEditServiceModalOpen(true)}
                  data-testid="button-update-time-sidebar"
                >
                  <CalendarClock className="w-4 h-4 mr-1.5" />
                  Update order time
                </Button>
              </div>
            )}
            {cart.length === 0 ? (
              <div className="text-center text-muted-foreground py-12">
                <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">Your cart is empty</p>
                <p className="text-xs mt-1">Add items from the menu to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map((cartItem, index) => (
                  <Card key={index}>
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">{cartItem.item.name}</h4>
                          {(Object.keys(cartItem.selections).length > 0 || cartItem.specialInstructions) && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {Object.entries(cartItem.selections).map(([groupId, optionIds]) => {
                                const group = cartItem.item.modifiers?.find(g => g.id === groupId);
                                if (!group) return null;
                                const selectedOptions = group.options.filter(opt => optionIds.includes(opt.id));
                                return (
                                  <div key={groupId}>
                                    {selectedOptions.map(opt => opt.name).join(', ')}
                                  </div>
                                );
                              })}
                              {cartItem.specialInstructions && (
                                <div className="italic mt-0.5">"{cartItem.specialInstructions}"</div>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => removeFromCart(index)}
                              data-testid={`button-sidebar-cart-minus-${index}`}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="font-medium w-5 text-center text-sm">{cartItem.quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              disabled={isOrderTimeExpired}
                              onClick={() => addOneToCart(index)}
                              data-testid={`button-sidebar-cart-plus-${index}`}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 ml-auto"
                              onClick={() => {
                                setEditingCartIndex(index);
                                setSelectedItem(cartItem.item);
                              }}
                              data-testid={`button-sidebar-edit-${index}`}
                            >
                              <PenLine className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => setCart(cart.filter((_, i) => i !== index))}
                              data-testid={`button-sidebar-remove-${index}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-sm">${(cartItem.totalPrice * cartItem.quantity).toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">${cartItem.totalPrice.toFixed(2)} ea</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          {cart.length > 0 && (
            <div className="border-t bg-background flex-shrink-0">
              {/* Order context bar */}
              {isOrderingMode && (
                <div className="px-4 py-2 border-b">
                  <div className="flex items-center gap-2 text-sm">
                    {urlParams.serviceType === 'delivery' ? (
                      <Truck className="w-4 h-4 text-kudoz flex-shrink-0" />
                    ) : (
                      <Package className="w-4 h-4 text-kudoz flex-shrink-0" />
                    )}
                    <span className="font-medium capitalize">{urlParams.serviceType}</span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground truncate text-xs">
                      {urlParams.location?.split(',')[0] || 'Address'}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 ml-auto"
                      onClick={() => setIsEditServiceModalOpen(true)}
                      data-testid="button-edit-service-sidebar"
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}
              {/* Total + Kudoz */}
              <div className="px-4 py-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Total</span>
                    <span className="text-xl font-bold">${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-kudoz">
                    <KudozIcon size="sm" />
                    <span className="font-medium">+{Math.floor(cartTotal * 7)}</span>
                  </div>
                </div>
                {/* Minimum order warning — sidebar */}
                {belowMinOrder > 0 && (
                  <div className="flex items-center gap-2 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 px-3 py-2" data-testid="min-order-warning-sidebar">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                    <p className="text-xs text-amber-800 dark:text-amber-300">
                      Minimum order ${restaurant!.minOrder!.toFixed(2)} — add{' '}
                      <span className="font-semibold">${belowMinOrder.toFixed(2)}</span> more
                    </p>
                  </div>
                )}
                <Button
                  className="w-full"
                  size="lg"
                  disabled={isOrderTimeExpired || belowMinOrder > 0}
                  onClick={() => {
                    if (urlParams.serviceType) {
                      localStorage.setItem('eatzy-order-config', JSON.stringify({
                        serviceType: urlParams.serviceType,
                        timing: urlParams.timing || 'asap',
                        scheduledTime: urlParams.scheduledTime || null,
                        addressId: urlParams.addressId || orderConfig?.addressId || null,
                      }));
                    }
                    if (!isAuthenticated) {
                      setGuestCheckoutSheetOpen(true);
                      return;
                    }
                    const serviceType = urlParams.serviceType || orderConfig?.serviceType || 'delivery';
                    const hasPhone = phones.length > 0;
                    const hasAddress = serviceType === 'delivery'
                      ? !!(urlParams.addressId || orderConfig?.addressId || primaryAddress)
                      : true;
                    if (!hasPhone && !isProfileLoading) {
                      setPreCheckoutNewPhone('');
                      setShowPreCheckoutModal(true);
                      return;
                    }
                    if (!hasAddress) {
                      toast({
                        title: 'Delivery address required',
                        description: 'Please add a delivery address before checking out.',
                        variant: 'destructive',
                      });
                      return;
                    }
                    navigate('/checkout');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  data-testid="button-sidebar-checkout"
                >
                  {belowMinOrder > 0
                    ? `Add $${belowMinOrder.toFixed(2)} more`
                    : 'Checkout'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Guest Checkout Sheet — shown when unauthenticated user taps Checkout */}
      <Sheet
        open={guestCheckoutSheetOpen}
        onOpenChange={(open) => {
          setGuestCheckoutSheetOpen(open);
          if (!open) {
            setShowGuestForm(false);
            setGuestServerError('');
            setEmailExistsWarning(false);
          }
        }}
      >
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[90vh] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle>Sign in to complete your order</SheetTitle>
          </SheetHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Create a free Kudoz account to place orders, earn Kudoz rewards, and save your favourites.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                className="w-full bg-[#0D9C97] text-white"
                onClick={() => {
                  setGuestCheckoutSheetOpen(false);
                  const fullPath = currentLocation + (searchString ? `?${searchString}` : '');
                  const returnUrl = encodeURIComponent(fullPath);
                  navigate(`/signup?returnUrl=${returnUrl}`);
                }}
              >
                Sign Up
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setGuestCheckoutSheetOpen(false);
                  const fullPath = currentLocation + (searchString ? `?${searchString}` : '');
                  const returnUrl = encodeURIComponent(fullPath);
                  navigate(`/login?returnUrl=${returnUrl}`);
                }}
              >
                Log In
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">or</span>
              </div>
            </div>

            <button
              type="button"
              className="w-full flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
              onClick={() => { setShowGuestForm(prev => !prev); setGuestServerError(''); }}
            >
              <span>Continue without logging in</span>
              <ChevronDown className={cn('h-4 w-4 transition-transform', showGuestForm && 'rotate-180')} />
            </button>

            {showGuestForm && (
              <form onSubmit={handleGuestCheckoutSubmit} className="space-y-3 pt-1">
                {guestServerError && (
                  <Alert variant="destructive">
                    <AlertDescription>{guestServerError}</AlertDescription>
                  </Alert>
                )}
                {emailExistsWarning && (
                  <Alert>
                    <AlertDescription className="text-sm">
                      An account with this email already exists.{' '}
                      <button
                        type="button"
                        className="font-semibold underline"
                        onClick={() => {
                          setGuestCheckoutSheetOpen(false);
                          const fullPath = currentLocation + (searchString ? `?${searchString}` : '');
                          const returnUrl = encodeURIComponent(fullPath);
                          navigate(`/login?returnUrl=${returnUrl}`);
                        }}
                      >
                        Sign in instead
                      </button>
                    </AlertDescription>
                  </Alert>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="guest-checkout-firstName" className="text-xs">First name</Label>
                    <Input
                      id="guest-checkout-firstName"
                      placeholder="Jane"
                      value={guestFields.firstName}
                      onChange={handleGuestFieldChange('firstName')}
                      disabled={guestLoading}
                    />
                    {guestErrors.firstName && <p className="text-xs text-destructive">{guestErrors.firstName}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="guest-checkout-lastName" className="text-xs">Last name</Label>
                    <Input
                      id="guest-checkout-lastName"
                      placeholder="Doe"
                      value={guestFields.lastName}
                      onChange={handleGuestFieldChange('lastName')}
                      disabled={guestLoading}
                    />
                    {guestErrors.lastName && <p className="text-xs text-destructive">{guestErrors.lastName}</p>}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="guest-checkout-phone" className="text-xs">Phone number</Label>
                  <Input
                    id="guest-checkout-phone"
                    type="tel"
                    placeholder="(555) 000-0000"
                    value={guestFields.phone}
                    onChange={handleGuestFieldChange('phone')}
                    disabled={guestLoading}
                  />
                  {guestErrors.phone && <p className="text-xs text-destructive">{guestErrors.phone}</p>}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="guest-checkout-email" className="text-xs">Email address</Label>
                  <Input
                    id="guest-checkout-email"
                    type="email"
                    placeholder="jane@example.com"
                    value={guestFields.email}
                    onChange={handleGuestFieldChange('email')}
                    onBlur={handleGuestEmailBlur}
                    disabled={guestLoading}
                  />
                  {guestErrors.email && <p className="text-xs text-destructive">{guestErrors.email}</p>}
                </div>
                <Button type="submit" className="w-full" disabled={guestLoading}>
                  {guestLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Continue to Checkout
                </Button>
              </form>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
